import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  Renderer2,
  HostListener,
  ChangeDetectorRef,
  AfterViewInit,
  CUSTOM_ELEMENTS_SCHEMA
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, take } from 'rxjs';
import { NgIf } from '@angular/common';
import { DataService, Card, Level1Topic, Level2Topic, Level3Topic, TopicItem, TopicsResponse, TopicTreeResponse, TopicTreeNode } from '../../services/data.service';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { NavigationAppComponent } from './navigation-app/navigation-app.component';
import { MainAreaComponent } from './main-area/main-area.component';
import { DetailsAreaComponent } from './details-area/details-area.component';
import { AppHeaderComponent } from './app-header/app-header.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LayoutSwitcherComponent } from './layout-switcher/layout-switcher.component';
import { SingleTreeComponent } from './single-tree/single-tree.component';
import { FontSizeService, FontSizeOption } from '../../services/font-size.service';
import { finalize, takeUntil, catchError, tap } from 'rxjs/operators';
import { NavigationPathService } from '../../services/navigation-path.service';
import { TreeService } from '../../services/tree.service';
import { OcrJsonLoaderService } from '../../services/ocr-json-loader.service';

// Extend NavigationAppComponent interface to include missing methods
declare module './navigation-app/navigation-app.component' {
  interface NavigationAppComponent {
    collapsePaneOne(collapse: boolean): void;
    collapsePaneTwo(collapse: boolean): void;
  }
}

@Component({
  selector: 'app-home',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    NgIf,
    NavigationAppComponent,
    MainAreaComponent,
    DetailsAreaComponent,
    AppHeaderComponent,
    MatProgressSpinnerModule,
    LayoutSwitcherComponent,
    SingleTreeComponent
  ],
  templateUrl: './app-home.component.html',
  styleUrls: ['./app-home.component.css']
})
export class AppHomeComponent implements OnInit, OnDestroy, AfterViewInit {
  // Define default pane width constants
  readonly DEFAULT_NAV_WIDTH = 25;   // 20% of the available space
  readonly DEFAULT_MAIN_WIDTH = 40;  // 40% of the available space
  readonly DEFAULT_DETAILS_WIDTH = 35; // 40% of the available space
  readonly COLLAPSED_NAV_WIDTH = 60; // 60px (fixed width)
  readonly COLLAPSED_MAIN_DETAILS_WIDTH = 47.5; // 47.5% of remaining space after nav is collapsed

  userName = 'DemoUser';
  userEmail = 'demo@example.com';
  userRole = 'demo';

  subjectChanged = new Subject<string>();

  // Navigation collapse state
  isNavigationCollapsed = false;

  // Pane-1 navigation state
  p1Mode: 'topicwise' | 'yearwise' = 'topicwise';
  selectedSubject: string | null = null;
  selectedSubItem: string | null = null;
  selectedTopic: string | null = null;
  selectedSubTopic: string | null = null;
  selectedYearwiseExam: string | null = null;
  selectedYearwiseLower: string | null = null;
  selectedYear: string | null = null;

  // Data arrays
  p1Subjects: string[] = [];
  p1SubMap: {[key: string]: string[]} = {};
  p2TopicsMap: {[key: string]: string[]} = {};
  p2SubTopicsMap: {[key: string]: string[]} = {};
  p1YearwiseExams: string[] = []; // Clear hardcoded data
  p1YearwiseLower: {[key: string]: string[]} = {}; // Clear hardcoded data
  p2Years: {[key: string]: string[]} = {};

  // Loading states
  isLoadingSubjects = false;
  isLoadingSections = false;
  isLoadingTopics = false;
  isLoadingSubtopics = false;
  isLoadingTopicQuestions = false;

  // Currently selected card (for Pane 4 details)
  selectedCard: Card | null = null;

  // Resizable divider properties
  isResizing = false;
  mainAreaFlex = `0 0 ${this.DEFAULT_MAIN_WIDTH}%`;
  detailsAreaFlex = `0 0 ${this.DEFAULT_DETAILS_WIDTH}%`;
  private savedMainAreaFlex = '';
  private savedDetailsAreaFlex = '';
  startX = 0;
  currentMainWidth = this.DEFAULT_MAIN_WIDTH;
  currentDetailsWidth = this.DEFAULT_DETAILS_WIDTH;
  showResetNotification = false;
  readonly MIN_MAIN_WIDTH = 20; // Minimum width for main area (%)
  readonly MAX_MAIN_WIDTH = 60; // Maximum width for main area (%)
  readonly NAV_WIDTH = this.DEFAULT_NAV_WIDTH; // Width of navigation area (%)

  isDragging = false;

  // New mapping to keep track of which subtopic belongs to which topic
  subtopicToTopicMap: {[subtopic: string]: string} = {};

  // Map to store subject IDs by name for API calls
  subjectIdMap: {[subjectName: string]: string} = {};

  // Map to store level1 topic IDs
  level1TopicIdMap: {[topicName: string]: string} = {};

  // Map to store level2 topic IDs
  level2TopicIdMap: {[topicName: string]: string} = {};

  // Map to store level3 topic IDs
  level3TopicIdMap: {[topicName: string]: string} = {};

  // Add this property to the class
  loadedQuestions: Card[] = [];

  // Add ViewChild reference to the navigation-app component
  @ViewChild('navigationApp') navigationApp!: NavigationAppComponent;

  // Map to store topic IDs by name for API calls - supports any level
  topicIdMap: { [topicName: string]: { id: string, level: number, hasChildren: boolean, hindiName?: string } } = {};

  // Add the missing properties
  isPaneOne_Collapsed = false;
  isPaneTwo_Collapsed = false;

  // Show details toggle property
  showDetails = true; // Default to showing details pane

  // Current language selection (synced from main-area)
  currentLanguage: string = 'bilingual';

  // New property for single tree layout
  showSingleTree = false;

  // Hindi name mapping
  hindiNameMap: {[key: string]: string} = {};

  // Add ViewChild reference to the single-tree component
  @ViewChild('singleTreeComponent') singleTreeComponent!: SingleTreeComponent;

  // Add this property to track whether to show main area when in fullscreen mode
  showMains = true;

  // View Mode property
  viewMode: 'split' | 'fullscreen' = 'split';

  // Mobile view properties
  isMobileView = false;
  showMobileNav = false;
  showMobileDetails = false;

  // Add destroy Subject for subscription management
  private destroy$ = new Subject<void>();

  // Add currentFontSize property
  currentFontSize: FontSizeOption = 'medium';

  // Navigation area flex property - updated to use constant
  navigationAreaFlex = `0 0 ${this.DEFAULT_NAV_WIDTH}%`;

  // Resize state for navigation divider
  isNavDragging = false;
  startNavX = 0;
  currentNavWidth = this.DEFAULT_NAV_WIDTH; // Current width percentage of navigation area

  // Limits for navigation area width
  readonly MIN_NAV_WIDTH = 25; // Minimum width for navigation area
  readonly MAX_NAV_WIDTH = 40; // Maximum width for navigation area - allowing expansion up to 30%

  // Track current layout
  currentLayout: string = 'default';

  // Control initialization sequence
  initializeMainArea = false;
  initializeDetailsArea = false;

  // Track if a refresh is already in progress to prevent multiple refreshes
  private isRefreshingNavigationTree = false;

  constructor(
    private dataService: DataService,
    private router: Router,
    private authService: AuthService,
    private renderer: Renderer2,
    private fontSizeService: FontSizeService,
    private cdr: ChangeDetectorRef,
    private navigationPathService: NavigationPathService,
    private treeService: TreeService,
    private ocrJsonLoaderService: OcrJsonLoaderService
  ) {
    console.log('AppHomeComponent constructor called');
  }

  ngOnInit(): void {
    console.log('AppHomeComponent ngOnInit called');

    // Clear any saved panel sizes to ensure we use our constants
    this.clearSavedPanelSizes();
    console.log('localStorage cleared');

    // Initialize pane widths programmatically
    this.initializePaneWidths();
    console.log('After initializePaneWidths:');
    this.logCurrentWidthSettings();

    // Reset to default view to ensure navigation is shown
    this.resetToDefaultView();
    console.log('After resetToDefaultView:');
    this.logCurrentWidthSettings();

    // Register event listeners for resize tracking
    this.registerEventListeners();

    // Rest of the ngOnInit code...
    // Control initialization sequence
    // First initialize AppHomeComponent fully before triggering child components
    setTimeout(() => {
      // Force refresh the tree data from the API - this will trigger NavigationAppComponent initialization
      console.log('Forcing tree refresh from API on application start');
      this.treeService.refreshTree().then(() => {
        console.log('Initial tree refresh completed');
        // Force reset to default view again after tree refresh
        this.resetToDefaultView();
        console.log('After second resetToDefaultView:');
        this.logCurrentWidthSettings();

        // Wait for navigation to be ready before initializing other components
        setTimeout(() => {
          // Let the template know it's time to initialize main-area component
          this.initializeMainArea = true;

          // Only after main-area is initialized, initialize details-area
          setTimeout(() => {
            this.initializeDetailsArea = true;
            this.cdr.detectChanges();
            console.log('After component initialization:');
            this.logCurrentWidthSettings();
          }, 100);

          this.cdr.detectChanges();
        }, 100);
      }).catch(error => {
        console.error('Error during initial tree refresh:', error);
        // Even on error, ensure default view
        this.resetToDefaultView();
      });
    }, 0);

    // Rest of the original ngOnInit code...

    // Check if we're in mobile view and set appropriate flags
    this.checkIfMobileView();

    // Subscribe to window resize events to update mobile view status
    window.addEventListener('resize', this.onResize.bind(this));

    // Subscribe to font size changes
    this.fontSizeService.fontSize$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(size => {
      this.currentFontSize = size;
    });

    // Subscribe to tree data changes
    this.treeService.getTreeData().pipe(
      takeUntil(this.destroy$)
    ).subscribe(treeData => {
      console.log('Tree data updated from BehaviorSubject');
      // Check if the tree data is valid
      if (!treeData || !treeData.subjects || Object.keys(treeData.subjects).length === 0) {
        console.warn('Received empty tree data from BehaviorSubject, attempting refresh again');
        // Try refreshing again if data is empty
        setTimeout(() => {
          this.treeService.refreshTree().catch(error => {
            console.error('Error during tree refresh after receiving empty data:', error);
          });
        }, 2000);
        return;
      }

      this.updateUIWithTreeData(treeData);
    });
  }

  /**
   * Initialize pane widths programmatically
   * This ensures consistent initialization across the application
   */
  private initializePaneWidths(): void {
    // Set initial flex values based on default constants
    this.navigationAreaFlex = `0 0 ${this.DEFAULT_NAV_WIDTH}%`;
    this.mainAreaFlex = `0 0 ${this.DEFAULT_MAIN_WIDTH}%`;
    this.detailsAreaFlex = `0 0 ${this.DEFAULT_DETAILS_WIDTH}%`;

    // Set current width values
    this.currentNavWidth = this.DEFAULT_NAV_WIDTH;
    this.currentMainWidth = this.DEFAULT_MAIN_WIDTH;
    this.currentDetailsWidth = this.DEFAULT_DETAILS_WIDTH;

    // Log current width settings after initialization
    console.log('Panel widths initialized to constants:');
    this.logCurrentWidthSettings();
  }

  // Method to update pane widths when navigation is collapsed/expanded
  updatePaneWidthsOnNavigationChange(): void {
    if (this.isNavigationCollapsed) {
      // When navigation is collapsed, set its width to fixed 60px
      this.navigationAreaFlex = `0 0 60px`;

      // Adjust main and details areas to share remaining space
      const sharedWidth = this.COLLAPSED_MAIN_DETAILS_WIDTH;
      this.mainAreaFlex = `0 0 ${sharedWidth}%`;
      this.detailsAreaFlex = `0 0 ${sharedWidth}%`;

      // Update current widths for dragging calculations
      this.currentMainWidth = sharedWidth;
      this.currentDetailsWidth = sharedWidth;
    } else {
      // When navigation is expanded, restore default widths
      this.navigationAreaFlex = `0 0 ${this.currentNavWidth}%`;
      this.mainAreaFlex = `0 0 ${this.currentMainWidth}%`;
      this.detailsAreaFlex = `0 0 ${this.currentDetailsWidth}%`;
    }

    // Force change detection to update UI
    this.cdr.detectChanges();
  }

  // Override the existing resetPanelSizes method to use our constants
  resetPanelSizes(): void {
    // Reset to default proportions
    this.currentMainWidth = this.DEFAULT_MAIN_WIDTH;
    this.currentDetailsWidth = this.DEFAULT_DETAILS_WIDTH;
    this.mainAreaFlex = `0 0 ${this.DEFAULT_MAIN_WIDTH}%`;
    this.detailsAreaFlex = `0 0 ${this.DEFAULT_DETAILS_WIDTH}%`;

    // Show reset notification
    this.showResetNotification = true;
    setTimeout(() => {
      this.showResetNotification = false;
      this.cdr.detectChanges();
    }, 2000);

    // Save the updated panel sizes
    this.savePanelSizes();
    this.cdr.detectChanges();
  }

  // Add override for resetNavPanelSize method
  resetNavPanelSize(): void {
    // Reset navigation panel to default width
    this.currentNavWidth = this.DEFAULT_NAV_WIDTH;
    this.navigationAreaFlex = `0 0 ${this.DEFAULT_NAV_WIDTH}%`;

    // Show reset notification
    this.showResetNotification = true;
    setTimeout(() => {
      this.showResetNotification = false;
      this.cdr.detectChanges();
    }, 2000);

    // Save the updated navigation panel size
    this.saveNavPanelSize();
    this.cdr.detectChanges();
  }

  // Override the onNavigationCollapseChange method to update panel widths
  onNavigationCollapseChange(isCollapsed: boolean): void {
    console.log('Navigation collapse changed to:', isCollapsed);
    this.isNavigationCollapsed = isCollapsed;

    // Update pane widths based on new collapse state
    this.updatePaneWidthsOnNavigationChange();

    // Additional code from original method...
    if (this.navigationApp) {
      if (isCollapsed) {
        this.isPaneOne_Collapsed = true;
        this.isPaneTwo_Collapsed = true;
        this.navigationApp.collapsePaneOne(true);
        this.navigationApp.collapsePaneTwo(true);
      } else {
        this.isPaneOne_Collapsed = false;
        this.isPaneTwo_Collapsed = false;
        this.navigationApp.collapsePaneOne(false);
        this.navigationApp.collapsePaneTwo(false);
      }
    }

    // Force a resize event to update all components
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }

  ngOnDestroy(): void {
    // Clean up event listeners if they exist
    if (this.isDragging) {
      document.removeEventListener('mousemove', this.onMouseMove.bind(this));
      document.removeEventListener('mouseup', this.onMouseUp.bind(this));
    }

    // Remove resize listener
    window.removeEventListener('resize', this.onWindowResize.bind(this));

    this.destroy$.next();
    this.destroy$.complete();

    // Unregister event listeners to prevent memory leaks
    this.unregisterEventListeners();
  }

  // Load saved panel sizes from local storage
  loadPanelSizes() {
    try {
      console.log('Attempting to load panel sizes from localStorage');

      // Load main/details panel sizes
      const savedMainFlex = localStorage.getItem('mainAreaFlex');
      const savedDetailsFlex = localStorage.getItem('detailsAreaFlex');

      if (savedMainFlex && savedDetailsFlex) {
        // Extract percentage value properly
        const mainWidthMatch = savedMainFlex.match(/0 0 (\d+(?:\.\d+)?)%/);
        if (mainWidthMatch && mainWidthMatch[1]) {
          const mainWidth = parseFloat(mainWidthMatch[1]);
          if (!isNaN(mainWidth)) {
            this.mainAreaFlex = savedMainFlex;
            this.currentMainWidth = mainWidth;
            console.log(`Loaded main width: ${mainWidth}%`);
          }
        }

        // Extract percentage value properly
        const detailsWidthMatch = savedDetailsFlex.match(/0 0 (\d+(?:\.\d+)?)%/);
        if (detailsWidthMatch && detailsWidthMatch[1]) {
          const detailsWidth = parseFloat(detailsWidthMatch[1]);
          if (!isNaN(detailsWidth)) {
            this.detailsAreaFlex = savedDetailsFlex;
            this.currentDetailsWidth = detailsWidth;
            console.log(`Loaded details width: ${detailsWidth}%`);
          }
        }
      } else {
        console.log('No saved main/details panel sizes found');
      }

      // Load navigation panel size
      const savedNavFlex = localStorage.getItem('navigationAreaFlex');
      if (savedNavFlex) {
        // Extract percentage value properly
        const navWidthMatch = savedNavFlex.match(/0 0 (\d+(?:\.\d+)?)%/);
        if (navWidthMatch && navWidthMatch[1]) {
          const navWidth = parseFloat(navWidthMatch[1]);
          if (!isNaN(navWidth)) {
            this.navigationAreaFlex = savedNavFlex;
            this.currentNavWidth = navWidth;
            console.log(`Loaded navigation width: ${navWidth}%`);
          }
        } else {
          // If saving in pixels (like for collapsed navigation)
          if (savedNavFlex.includes('60px')) {
            this.navigationAreaFlex = savedNavFlex;
            console.log('Loaded collapsed navigation width: 60px');
          } else {
            // Fallback to default
            this.navigationAreaFlex = `0 0 ${this.DEFAULT_NAV_WIDTH}%`;
            this.currentNavWidth = this.DEFAULT_NAV_WIDTH;
            console.log(`Using default navigation width: ${this.DEFAULT_NAV_WIDTH}%`);
          }
        }
      } else {
        console.log('No saved navigation panel size found');
        this.navigationAreaFlex = `0 0 ${this.DEFAULT_NAV_WIDTH}%`;
        this.currentNavWidth = this.DEFAULT_NAV_WIDTH;
      }

      // Log current width settings after loading
      console.log('After loading panel sizes:');
      this.logCurrentWidthSettings();
    } catch (e) {
      console.error('Error loading panel sizes:', e);
      // Reset to defaults if there was an error
      this.resetPanelSizes();
      this.resetNavPanelSize();
    }
  }

  // Save panel sizes to localStorage
  private savePanelSizes(): void {
    try {
      // Parse the width values from the flex strings
      const mainWidthStr = this.mainAreaFlex.split(' ')[2];
      const detailsWidthStr = this.detailsAreaFlex.split(' ')[2];

      // Remove the '%' character and parse as float
      const mainWidth = parseFloat(mainWidthStr);
      const detailsWidth = parseFloat(detailsWidthStr);

      // Ensure we have valid numbers
      if (!isNaN(mainWidth) && !isNaN(detailsWidth)) {
        localStorage.setItem('panelSizes', JSON.stringify({
          mainWidth: parseFloat(mainWidth.toFixed(2)),
          detailsWidth: parseFloat(detailsWidth.toFixed(2))
        }));

        console.log(`Saved panel sizes - Main: ${mainWidth.toFixed(2)}%, Details: ${detailsWidth.toFixed(2)}%`);
      }
    } catch (error) {
      console.error('Error saving panel sizes:', error);
    }
  }

  // Start resize operation when mouse down on the divider
  startResize(event: MouseEvent): void {
    event.preventDefault();
    this.isResizing = true;
    this.startX = event.clientX;

    // Get the current widths directly from the flex properties
    // Make sure to parse them correctly
    const mainFlexValue = this.mainAreaFlex.split('%')[0];
    const detailsFlexValue = this.detailsAreaFlex.split('%')[0];

    this.currentMainWidth = parseFloat(mainFlexValue);
    this.currentDetailsWidth = parseFloat(detailsFlexValue);

    // Make sure we have valid numbers
    if (isNaN(this.currentMainWidth)) this.currentMainWidth = 40;
    if (isNaN(this.currentDetailsWidth)) this.currentDetailsWidth = 40;

    // Add global mouse move and up event listeners
    this.renderer.listen('document', 'mousemove', this.onMouseMove);
    this.renderer.listen('document', 'mouseup', this.onMouseUp);

    // Log the start position for debugging
    console.log(`Resize started - Main: ${this.currentMainWidth}%, Details: ${this.currentDetailsWidth}%`);
  }

  // Handle mouse move during resize
  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;

    // Calculate how much the mouse has moved
    const dx = event.clientX - this.startX;

    // Get the container width
    const container = document.querySelector('.app-content');
    if (!container) return;
    const containerWidth = container.getBoundingClientRect().width;

    // Convert mouse movement to percentage of container width
    const percentChange = (dx / containerWidth) * 100;

    // Calculate new width based on the original width plus the change
    let newMainWidth = this.currentMainWidth + percentChange;

    // Ensure the width is within bounds
    const constrainedWidth = Math.max(
      this.MIN_MAIN_WIDTH,
      Math.min(newMainWidth, this.MAX_MAIN_WIDTH)
    );

    // Update flex values
    this.mainAreaFlex = `0 0 ${constrainedWidth.toFixed(2)}%`;
    this.detailsAreaFlex = `0 0 ${(100 - this.NAV_WIDTH - constrainedWidth).toFixed(2)}%`;
  }

  // End resize operation when mouse up
  onMouseUp(): void {
    if (!this.isDragging) return;

    this.isDragging = false;

    // Remove event listeners
    document.removeEventListener('mousemove', this.onMouseMove.bind(this));
    document.removeEventListener('mouseup', this.onMouseUp.bind(this));

    // Get final width from the current flex style
    const mainWidth = parseFloat(this.mainAreaFlex.split(' ')[2]);
    if (!isNaN(mainWidth)) {
      // Update the current width so future drags start from this position
      this.currentMainWidth = mainWidth;

      // Ensure the width is within bounds
      const constrainedWidth = Math.max(
        this.MIN_MAIN_WIDTH,
        Math.min(mainWidth, this.MAX_MAIN_WIDTH)
      );

      // Update with constrained and formatted values
      this.mainAreaFlex = `0 0 ${constrainedWidth.toFixed(2)}%`;
      this.detailsAreaFlex = `0 0 ${(100 - this.NAV_WIDTH - constrainedWidth).toFixed(2)}%`;

      console.log(`Drag completed - Main: ${constrainedWidth.toFixed(2)}%, Details: ${(100 - this.NAV_WIDTH - constrainedWidth).toFixed(2)}%`);
    }

    // Save the new panel sizes
    this.savePanelSizes();
  }

  // Handle years list generated from NavigationAppComponent
  onYearsListGenerated(years: string[]): void {
    // If we have a selected paper type, store the years under that key
    if (this.selectedYearwiseLower) {
      this.p2Years[this.selectedYearwiseLower] = years;
    }

    // If we're in yearwise mode and have selected exam and paper but no year,
    // automatically select the first year
    if (this.p1Mode === 'yearwise' &&
        this.selectedYearwiseExam &&
        this.selectedYearwiseLower &&
        !this.selectedYear &&
        years.length > 0) {
      this.onYearSelect(years[0]);
    }
  }

  onYearwiseLowerSelect(lower: string): void {
    this.selectedYearwiseLower = lower;
    // Reset dependent selections
    this.selectedYear = null;

    // If years are available for the selected type, select the first one by default
    const yearsForType = this.p2Years[lower] || [];
    if (yearsForType.length > 0) {
      this.onYearSelect(yearsForType[0]);
    } else {
      // If years aren't loaded yet, wait and try again
      const checkInterval = setInterval(() => {
        const updatedYearsForType = this.p2Years[lower] || [];
        if (updatedYearsForType.length > 0) {
          clearInterval(checkInterval);
          this.onYearSelect(updatedYearsForType[0]);
        }
      }, 300);

      // Clear the interval after 3 seconds in case data never loads
      setTimeout(() => clearInterval(checkInterval), 3000);
    }
  }

  // Handle data loaded from NavigationAppComponent
  onSubjectsLoaded(subjects: string[]): void {
    this.p1Subjects = subjects;
  }

  onSectionsLoaded(data: {subject: string, sections: string[]}): void {
    console.log(`onSectionsLoaded called for subject: ${data.subject} with ${data.sections.length} sections:`, data.sections);
    this.p1SubMap[data.subject] = data.sections;

    // Forward to navigation app if available
    setTimeout(() => {
      if (this.navigationApp) {
        this.navigationApp.onSectionsLoadedFromParent(data);
      }
    }, 0);

    // Auto-select first section if this is the selected subject and no section is selected
    if (data.subject === this.selectedSubject && !this.selectedSubItem && data.sections.length > 0) {
      console.log(`Auto-selecting first section: ${data.sections[0]}`);
      this.selectedSubItem = data.sections[0];
      this.loadLevel2Topics(data.sections[0]);
    }
  }

  onTopicsLoaded(data: {section: string, topics: string[]}): void {
 //   console.log(`onTopicsLoaded called for section: ${data.section} with ${data.topics.length} topics:`, data.topics);
    this.p2TopicsMap[data.section] = data.topics;

    // Forward to navigation app if available
    setTimeout(() => {
      if (this.navigationApp) {
        this.navigationApp.onTopicsLoadedFromParent(data);
      }
    }, 0);

    // Auto-select first topic if this is the selected section and no topic is selected
    if (data.section === this.selectedSubItem && !this.selectedTopic && data.topics.length > 0) {
      console.log(`Auto-selecting first topic: ${data.topics[0]}`);
      this.selectedTopic = data.topics[0];
      this.loadLevel3Topics(data.topics[0]);
    }
  }

  onSubtopicsLoaded(data: {topic: string, subtopics: string[]}): void {
   // console.log(`onSubtopicsLoaded called for topic: ${data.topic} with ${data.subtopics.length} subtopics:`, data.subtopics);
    this.p2SubTopicsMap[data.topic] = data.subtopics;

    // Forward to navigation app if available
    setTimeout(() => {
      if (this.navigationApp) {
        this.navigationApp.onSubtopicsLoadedFromParent(data);
      }
    }, 0);

    // Update the subtopic-to-topic map for proper parent-child relationships
    data.subtopics.forEach(subtopic => {
      this.subtopicToTopicMap[subtopic] = data.topic;
    });

    // Auto-select first subtopic if this is the selected topic and no subtopic is selected
    if (data.topic === this.selectedTopic && !this.selectedSubTopic && data.subtopics.length > 0) {
      console.log(`Auto-selecting first subtopic: ${data.subtopics[0]}`);
      this.selectedSubTopic = data.subtopics[0];
    }
  }

  // Handle navigation events from NavigationAppComponent and SingleTreeComponent
  onP1ModeChange(mode: 'topicwise' | 'yearwise' | string): void {
    // Validate the mode to ensure it's either 'topicwise' or 'yearwise'
    if (mode !== 'topicwise' && mode !== 'yearwise') {
      console.error(`Invalid mode: ${mode}. Must be 'topicwise' or 'yearwise'`);
      return;
    }

    // Cast to the correct type
    const typedMode = mode as 'topicwise' | 'yearwise';
    this.p1Mode = typedMode;

    // Reset all selections
    this.selectedSubject = null;
    this.selectedSubItem = null;
    this.selectedTopic = null;
    this.selectedSubTopic = null;
    this.selectedYearwiseExam = null;
    this.selectedYearwiseLower = null;
    this.selectedYear = null;

    // Initialize appropriate mode
    if (typedMode === 'topicwise') {
      // Auto-select first subject if available
      if (this.p1Subjects.length > 0) {
        this.onSubjectSelect(this.p1Subjects[0]);
      }
    } else if (typedMode === 'yearwise') {
      // Auto-select first exam if available
      if (this.p1YearwiseExams.length > 0) {
        this.onYearwiseExamSelect(this.p1YearwiseExams[0]);
      } else {
        // If no exams are loaded yet, schedule a check for when they are
        const checkInterval = setInterval(() => {
          if (this.p1YearwiseExams.length > 0) {
            clearInterval(checkInterval);
            this.onYearwiseExamSelect(this.p1YearwiseExams[0]);
          }
        }, 500);

        // Clear the interval after 5 seconds in case data never loads
        setTimeout(() => clearInterval(checkInterval), 5000);
      }
    }
  }

  onSubjectSelect(subject: string): void {
    console.log(`Subject selected: ${subject}`);

/*    // If this is a different subject than previously selected, clear all dependent selections
    if (this.selectedSubject !== subject) {
      console.log(`Changing selected subject from ${this.selectedSubject} to ${subject}`);
      this.selectedSubject = subject;

      // Reset all child selections
      this.selectedSubItem = null;
      this.selectedTopic = null;
      this.selectedSubTopic = null;

      // Clear loaded questions
      this.loadedQuestions = [];
    }*/

    this.selectedSubject = subject;

    // Reset all child selections
    this.selectedSubItem = null;
    this.selectedTopic = null;
    this.selectedSubTopic = null;

    // Clear loaded questions
    this.loadedQuestions = [];

    // Check if we have the subject ID in our map
    const subjectId = this.getSubjectId(subject);
    if (subjectId) {
      // Use the new method to load the complete topic tree
      this.loadTopicTree(subject, subjectId);

      // Auto-select first section if available
      setTimeout(() => {
        if (this.p1SubMap[subject] && this.p1SubMap[subject].length > 0) {
          const firstSection = this.p1SubMap[subject][0];
          console.log(`Auto-selecting first section: ${firstSection}`);
          this.onSubItemSelect(firstSection);
        }
      }, 500); // Give time for the tree to load
    } else {
      console.error(`No subject ID found for ${subject}`);
      // Fallback to the old method in case we don't have an ID
      this.loadLevel1Topics(subject);
    }
  }

  onSubItemSelect(subItem: string): void {
    console.log(`Section selected: ${subItem}`);

    // If this is a different section than previously selected, clear child selections
    if (this.selectedSubItem !== subItem) {
      console.log(`Changing selected section from ${this.selectedSubItem} to ${subItem}`);

      // Find the parent subject for this section
      let parentSubject = null;
      for (const subject of this.p1Subjects) {
        const sections = this.p1SubMap[subject] || [];
        if (sections.includes(subItem)) {
          parentSubject = subject;
          break;
        }
      }

      // If parent subject found, update it
      if (parentSubject && this.selectedSubject !== parentSubject) {
        console.log(`Updating parent subject from ${this.selectedSubject} to ${parentSubject}`);
        this.selectedSubject = parentSubject;
      }

      this.selectedSubItem = subItem;

      // Reset topic and subtopic selections when changing sections
      this.selectedTopic = null;
      this.selectedSubTopic = null;

      // Clear loaded questions
      this.loadedQuestions = [];
    }

    // If topics for this section are already loaded in our maps
    if (this.p2TopicsMap[subItem] && this.p2TopicsMap[subItem].length > 0) {
      console.log(`Using pre-loaded topics for section: ${subItem}`, this.p2TopicsMap[subItem]);

      // Select the first topic since we've cleared the selection above
      if (this.p2TopicsMap[subItem].length > 0) {
        const firstTopic = this.p2TopicsMap[subItem][0];
        console.log(`Auto-selecting first topic: ${firstTopic}`);
        this.onTopicSelect(firstTopic);
      } else {
        console.log(`No topics found for section: ${subItem}`);
      }
    } else {
      // Fallback to the old method if topics aren't preloaded
      console.log(`No pre-loaded topics found for section: ${subItem}, using legacy method`);
      this.loadLevel2Topics(subItem);
    }

    // Refresh the navigation tree to ensure UI is updated
    setTimeout(() => {
      this.refreshNavigationTree();
    }, 100);
  }

  // Force refresh the navigation tree
  refreshNavigationTree(): void {
    console.log('App-Home: Refreshing navigation tree UI only');

    // Prevent multiple concurrent refreshes
    if (this.isRefreshingNavigationTree) {
      console.log('App-Home: Navigation tree refresh already in progress, ignoring request');
      return;
    }

    this.isRefreshingNavigationTree = true;

    try {
      // For selection changes, only re-render local tree UI.
      // Do not call refreshTopicTree() here because that triggers full API reloads.
      if (this.showSingleTree && this.singleTreeComponent) {
        console.log('App-Home: Refreshing single tree UI from existing data');
        this.singleTreeComponent.updateTreeData();
      } else if (this.navigationApp) {
        console.log('App-Home: Refreshing navigation UI from existing data');
        this.navigationApp.updateTreeData();
        this.navigationApp.updateTopicTreeData();
        this.navigationApp.updateYearwiseTreeData();
      } else {
        console.log('App-Home: Cannot refresh tree UI - no component available');
      }
    } finally {
      this.isRefreshingNavigationTree = false;
    }
  }

  // Add a method to check if a topic has children by making an API call
  checkTopicForChildren(topicName: string, topicId: string, level: number): void {
    console.log(`Checking if topic ${topicName} has children...`);

    // Set loading indicator based on topic level
    if (level <= 1) {
      this.isLoadingTopics = true;
    } else {
      this.isLoadingSubtopics = true;
    }

    this.dataService.getTopicsByLevel(topicId, level)
      .pipe(
        finalize(() => {
          // Reset loading indicators
          this.isLoadingTopics = false;
          this.isLoadingSubtopics = false;
        })
      )
      .subscribe({
        next: (response) => {
          console.log(`API response for ${topicName}:`, response);
          // Update the hasChildren flag for this topic based on the API response
          // Use both the API's has_children flag and check if there are actual children in the response
          const hasChildren = response.has_children || response.children.length > 0;

          console.log(`API response for ${topicName}: has_children=${response.has_children}, actual children=${response.children.length}`);

          // Update the topic metadata
          if (this.topicIdMap[topicName]) {
            this.topicIdMap[topicName].hasChildren = hasChildren;
          } else {
            this.topicIdMap[topicName] = {
              id: topicId,
              level: level,
              hasChildren: hasChildren
            };
          }

          console.log(`Updated hasChildren for ${topicName}: ${hasChildren} (children count: ${response.children.length})`);

          // Force refresh the navigation tree with the updated metadata
          this.refreshNavigationTree();

          // If there are children, load them
          if (hasChildren && response.children.length > 0) {
            // Store the children in the appropriate maps
            const childNames = response.children.map(child => child.name_eng);

            // Also store child topic metadata
            response.children.forEach(child => {
              // Determine if this child should have children based on level
              let childHasChildren = false;

              if (child.item_level === 1) {
                // Topics at level 1 likely have subtopics
                childHasChildren = true;
              } else if (child.item_level === 2) {
                // Subtopics at level 2 might have children but need explicit check later
                childHasChildren = false;
              } else {
                // For deeper levels, we don't assume children by default
                childHasChildren = false;
              }

              // Store the child's metadata
              this.topicIdMap[child.name_eng] = {
                id: child.topic_id,
                level: child.item_level,
                hasChildren: childHasChildren
              };
            });

            if (level >= 2) {
              // For topics at level 2 or higher, store as subtopics
              this.p2SubTopicsMap[topicName] = childNames;

              // Update subtopics in the navigation app
              this.onSubtopicsLoaded({topic: topicName, subtopics: childNames});
            } else if (level === 1) {
              // For level 1 topics, store as topics
              this.p2TopicsMap[topicName] = childNames;

              // Update topics in the navigation app
              this.onTopicsLoaded({section: topicName, topics: childNames});
            }

            // Force another refresh after storing children
            this.refreshNavigationTree();
          } else if (hasChildren && response.children.length === 0) {
            // If has_children is true but no children were returned,
            // we might need to make another call to get children
            console.log(`Topic ${topicName} has children flag but no children in response, may need explicit load`);
          } else {
            // No children, load questions
            this.loadQuestionsForTopic(topicName, topicId);
          }
        },
        error: (error) => {
          console.error(`Error checking for children of ${topicName}:`, error);
        }
      });
  }

  onTopicSelect(topic: string): void {
    console.log(`Topic selected: ${topic}`);

    // If this is a different topic than previously selected, clear child selections
    if (this.selectedTopic !== topic) {
      console.log(`Changing selected topic from ${this.selectedTopic} to ${topic}`);
      this.selectedTopic = topic;

      // Reset subtopic selection when changing topics
      this.selectedSubTopic = null;

      // Clear loaded questions
      this.loadedQuestions = [];

      // Make sure navigation tree is refreshed to reflect selection
      setTimeout(() => {
        this.refreshNavigationTree();
      }, 100);
    }

    // If subtopics for this topic are already loaded in our maps
    if (this.p2SubTopicsMap[topic] && this.p2SubTopicsMap[topic].length > 0) {
      console.log(`Using pre-loaded subtopics for topic: ${topic}`, this.p2SubTopicsMap[topic]);

      // Select the first subtopic since we've cleared the selection above
      if (this.p2SubTopicsMap[topic].length > 0) {
        const firstSubtopic = this.p2SubTopicsMap[topic][0];
        console.log(`Auto-selecting first subtopic: ${firstSubtopic}`);
        this.onSubTopicSelect(firstSubtopic);
      } else {
        console.log(`No subtopics found for topic: ${topic}`);
      }
    } else {
      // Instead of making an API call, see if the topic has children in the topicIdMap
      const topicInfo = this.topicIdMap[topic];
      if (topicInfo) {
        if (topicInfo.hasChildren) {
          console.log(`Topic ${topic} should have children according to topicIdMap, but they're not loaded properly.`);
          // This shouldn't happen if the tree was loaded properly, but we'll handle it gracefully

          // Try to use data from the parent subject's tree instead of making a new API call
          const subjectId = this.getSubjectIdForTopic(topic);
          if (subjectId) {
            console.log(`Using parent subject's tree data for topic: ${topic}`);
            // Do not call loadLevel3Topics, as it would make a new API call
            // Instead, just handle the current topic directly
            this.handleTopicWithNoLoadedSubtopics(topic);
          } else {
            // Only as a last resort, call the legacy method
            console.log(`No parent subject found for topic: ${topic}, using legacy method as last resort`);
            this.loadLevel3Topics(topic);
          }
        } else {
          // Topic doesn't have children, load questions directly
          console.log(`Topic ${topic} doesn't have children according to topicIdMap, loading questions directly`);
          this.loadQuestionsForTopic(topic, topicInfo.id);

          // Make sure navigation tree is refreshed to reflect selection for a topic without subtopics
          setTimeout(() => {
            this.refreshNavigationTree();
          }, 100);
        }
      } else {
        // We don't have info about this topic, which shouldn't happen
        // But as a fallback, use the legacy method
        console.warn(`No topic info found in topicIdMap for ${topic}, using legacy method as fallback`);
        this.loadLevel3Topics(topic);
      }
    }
  }

  // Helper method to find the subject ID for a topic by traversing up the hierarchy
  private getSubjectIdForTopic(topicName: string): string {
    // Try to determine which subject this topic belongs to
    for (const subjectName in this.p1SubMap) {
      const sections = this.p1SubMap[subjectName];
      for (const section of sections) {
        if (this.p2TopicsMap[section] && this.p2TopicsMap[section].includes(topicName)) {
          // Found the section and subject this topic belongs to
          return this.getSubjectId(subjectName);
        }
      }
    }
    return '';
  }

  // Handle a topic with no loaded subtopics without making API calls
  private handleTopicWithNoLoadedSubtopics(topicName: string): void {
    console.log(`Handling topic with no loaded subtopics: ${topicName}`);

    // Since we don't have subtopics for this topic but should,
    // we'll just load questions for this topic directly
    const topicInfo = this.topicIdMap[topicName];
    if (topicInfo) {
      console.log(`Loading questions directly for topic: ${topicName}`);
      this.loadQuestionsForTopic(topicName, topicInfo.id);

      // Make sure navigation tree is refreshed to reflect selection for a topic without subtopics
      setTimeout(() => {
        this.refreshNavigationTree();
      }, 100);
    }
  }

  onSubTopicSelect(subtopic: string): void {
    console.log(`Subtopic selected: ${subtopic}`);

    // Update the selected subtopic
    this.selectedSubTopic = subtopic;

    // In mobile view, hide the navigation and show the main area when a subtopic is selected
    if (this.isMobileView) {
      console.log('Mobile view - hiding navigation to show main area');
      this.showMobileNav = false;
      // Ensure we're not showing details initially
      this.showMobileDetails = false;
    }

    // Get the topic ID for this subtopic to load questions
    const topicId = this.topicIdMap[subtopic]?.id;
    if (topicId) {
      console.log(`Loading questions for subtopic: ${subtopic} with ID: ${topicId}`);

      // Don't clear questions immediately - set loading state and only clear
      // when new questions load (or on error)
      this.loadQuestionsForTopic(subtopic, topicId);
    } else {
      console.error(`No topic ID found for subtopic: ${subtopic}`);
      // Only clear questions if we can't load new ones
      this.loadedQuestions = [];
      this.selectedCard = null;
    }
  }

  onYearwiseExamSelect(exam: string): void {
    this.selectedYearwiseExam = exam;
    // Reset dependent selections
    this.selectedYearwiseLower = null;
    this.selectedYear = null;

    // Load papers for the selected exam if needed
    // If papers are available, select the first one by default
    if (this.p1YearwiseLower[exam] && this.p1YearwiseLower[exam].length > 0) {
      this.onYearwiseLowerSelect(this.p1YearwiseLower[exam][0]);
    } else {
      // If papers aren't loaded yet, wait and try again
      const checkInterval = setInterval(() => {
        if (this.p1YearwiseLower[exam] && this.p1YearwiseLower[exam].length > 0) {
          clearInterval(checkInterval);
          this.onYearwiseLowerSelect(this.p1YearwiseLower[exam][0]);
        }
      }, 300);

      // Clear the interval after 3 seconds in case data never loads
      setTimeout(() => clearInterval(checkInterval), 3000);
    }
  }



  onYearSelect(year: string): void {
    this.selectedYear = year;

    // In mobile view, hide the navigation and show the main area when a year is selected
    if (this.isMobileView) {
      console.log('Mobile view - hiding navigation to show main area on year selection');
      this.showMobileNav = false;
      // Ensure we're not showing details initially
      this.showMobileDetails = false;
    }

    // If needed, trigger loading of content for the selected year
    this.loadYearContent(year);
  }

  // Helper function to load content for the selected year
  loadYearContent(year: string): void {
    // This would be replaced with actual API call if needed
    console.log(`Loading content for ${this.selectedYearwiseExam} - ${this.selectedYearwiseLower} - ${year}`);
    // You could add actual data loading logic here if needed
  }

  // Handle selected card changes from main-area
  onSelectedCardChange(card: Card | null): void {
    // Store the previously selected card to determine if this is an initial load or user selection
    const wasCardPreviouslySelected = this.selectedCard !== null;
    this.selectedCard = card;

    // In mobile view, when a card is selected, always show details
    if (this.isMobileView && card) {
      // In mobile view, we need to ensure showDetails is true and then show mobile details
      this.showDetails = true;
      this.toggleMobileDetails(true);

      // Log for debugging
      console.log('Card selected in mobile view, showing details:', card);
    } else {
      // In desktop mode
      // If a card was explicitly selected by user, show the details area
      if (card) {
        this.showDetails = true;
        console.log('Card selected in desktop view, showing details:', card);
      }
    }
  }



  // Handle logout
  onLogout(): void {
    this.authService.logout();
    this.router.navigate(['/']);
  }

  // Handle settings button click
  onSettingsClick(): void {
    console.log('Settings button clicked');
    // This method doesn't do anything yet as requested
    // Future implementation could open a settings dialog or navigate to settings page
  }

  // Handle keyboard events for the divider
  handleDividerKeydown(event: KeyboardEvent): void {
    // Get current main width as a number
    const currentMainWidth = parseFloat(this.mainAreaFlex.split('%')[0]);
    const currentDetailsWidth = parseFloat(this.detailsAreaFlex.split('%')[0]);

    let newMainWidth = currentMainWidth;
    let newDetailsWidth = currentDetailsWidth;

    const stepSize = 1; // Change size by 1% per key press

    switch(event.key) {
      case 'ArrowLeft':
        // Move divider left (shrink main area, expand details area)
        newMainWidth = Math.max(currentMainWidth - stepSize, this.MIN_MAIN_WIDTH);
        newDetailsWidth = 100 - newMainWidth - this.NAV_WIDTH;
        event.preventDefault();
        break;

      case 'ArrowRight':
        // Move divider right (expand main area, shrink details area)
        newMainWidth = Math.min(currentMainWidth + stepSize, this.MAX_MAIN_WIDTH);
        newDetailsWidth = 100 - newMainWidth - this.NAV_WIDTH;
        event.preventDefault();
        break;

      case 'Home':
        // Set main area to minimum
        newMainWidth = this.MIN_MAIN_WIDTH;
        newDetailsWidth = 100 - newMainWidth - this.NAV_WIDTH;
        event.preventDefault();
        break;

      case 'End':
        // Set main area to maximum
        newMainWidth = this.MAX_MAIN_WIDTH;
        newDetailsWidth = 100 - newMainWidth - this.NAV_WIDTH;
        event.preventDefault();
        break;

      case 'Enter':
      case ' ': // Space key
        // Reset to default
        this.resetPanelSizes();
        event.preventDefault();
        return;
    }

    // Apply the changes
    if (newMainWidth !== currentMainWidth) {
      this.mainAreaFlex = `0 0 ${newMainWidth}%`;
      this.detailsAreaFlex = `0 0 ${newDetailsWidth}%`;
      this.savePanelSizes();
    }
  }

  onMouseDown(event: MouseEvent): void {
    this.isDragging = true;
    event.preventDefault();

    // Store the starting position
    this.startX = event.clientX;

    // Get the current widths directly from the flex properties
    const mainWidth = parseFloat(this.mainAreaFlex.split(' ')[2]);
    this.currentMainWidth = isNaN(mainWidth) ? 40 : mainWidth;

    // Add event listeners for mouse move and up
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
  }

  // Load subjects from the new API with a single call that includes the complete topic structure
  loadSubjectsFromApi(): void {
    console.log('Loading complete topic structure from API...');
    this.isLoadingSubjects = true;

    // Use the new unified API for complete topic structure
    this.dataService.getCompleteTopicStructure()
      .pipe(
        finalize(() => {
          this.isLoadingSubjects = false;
          // isPreloading = false; - Removed to ensure navigation always shows
        })
      )
      .subscribe({
        next: (response) => {
          console.log('Complete topic structure API response:', response);

          if (response && response.success && response.topics && response.topics.length > 0) {
            // Extract subject names and IDs from the response
            const subjects = response.topics.map(topic => topic.name_eng);

            // Update the p1Subjects array
            this.p1Subjects = subjects;

            // Store IDs in the subjectIdMap for future API calls
            response.topics.forEach(topic => {
              this.subjectIdMap[topic.name_eng] = topic.topic_id;

              // Also update topicIdMap with the subject info
              this.topicIdMap[topic.name_eng] = {
                id: topic.topic_id,
                level: topic.item_level,
                hasChildren: true, // Assume subjects have children until proven otherwise
                hindiName: topic.name_hindi
              };
            });

            // Log the updated maps
            console.log('Updated subjectIdMap:', this.subjectIdMap);
            console.log('Updated subjects in topicIdMap:', this.topicIdMap);

            // Emit the loaded subjects
            this.onSubjectsLoaded(subjects);

            // Process all topic trees from the response
            if (response.trees && Object.keys(response.trees).length > 0) {
              Object.keys(response.trees).forEach(topicId => {
                const tree = response.trees[topicId];

                // Find the subject name for this tree
                const subjectName = this.findSubjectNameById(topicId);
                if (subjectName) {
                  console.log(`Processing tree for subject: ${subjectName}`);
                  this.processTopicTree(tree, subjectName);
                } else {
                  console.warn(`Could not find subject name for topic ID: ${topicId}`);
                }
              });
            }

            // Auto-select first subject if none selected
            if (!this.selectedSubject && this.p1Subjects.length > 0) {
              const firstSubject = this.p1Subjects[0];
              console.log(`Auto-selecting first subject: ${firstSubject}`);
              this.onSubjectSelect(firstSubject);
            }

            // Refresh the navigation tree after processing all trees
            this.refreshNavigationTree();
          } else {
            console.warn('No topics found in API response or response was not successful');
          }
        },
        error: (error) => {
          console.error('Error loading complete topic structure from API:', error);
        }
      });
  }

  // This method is no longer needed as we're loading everything in loadSubjectsFromApi
  // Keeping it as a no-op for compatibility
  loadAllSubjectTrees(): void {
    console.log('loadAllSubjectTrees called but is now a no-op - trees are loaded with subjects');
    // No operation - all trees are now loaded with the complete topic structure
  }

  // Helper to find subject name by topic ID
  findSubjectNameById(topicId: string): string | null {
    for (const [subjectName, id] of Object.entries(this.subjectIdMap)) {
      if (id === topicId) {
        return subjectName;
      }
    }
    return null;
  }

  // Load topics for a parent topic at a specific level
  loadTopics(parentName: string, parentId: string, level: number): void {
    console.log(`Loading topics for parent: ${parentName} (ID: ${parentId}) at level: ${level}`);

    // Set appropriate loading flag based on level
    switch (level) {
      case 0:
        this.isLoadingSections = true;
        break;
      case 1:
        this.isLoadingTopics = true;
        break;
      case 2:
        this.isLoadingSubtopics = true;
        break;
      default:
        // For higher levels, we could use the subtopics loading flag
        console.log(`Loading level ${level} topics...`);
        this.isLoadingSubtopics = true;
    }

    // Use the unified API method
    this.dataService.getTopicsByLevel(parentId, level)
      .pipe(
        finalize(() => {
          // Reset loading flag based on level
          switch (level) {
            case 0:
              this.isLoadingSections = false;
              break;
            case 1:
              this.isLoadingTopics = false;
              break;
            case 2:
              this.isLoadingSubtopics = false;
              break;
            default:
    //          console.log(`Finished loading level ${level} topics`);
              this.isLoadingSubtopics = false;
          }
        })
      )
      .subscribe({
        next: (response) => {
     //     console.log(`Received topics for parent: ${parentName}`, response);

          const topicNames = response.children.map(topic => topic.name_eng);
          const currentLevel = response.current_level;
          const nextLevel = response.next_level;

          // Store topic names in the appropriate map based on level or in a new map for deeper levels
          if (currentLevel === 0) {
            // For subjects (level 0)
            this.p1SubMap[parentName] = topicNames;
          } else if (currentLevel === 1) {
            // For sections (level 1)
            this.p2TopicsMap[parentName] = topicNames;
          } else if (currentLevel === 2) {
            // For topics (level 2)
            this.p2SubTopicsMap[parentName] = topicNames;

            // Map subtopics to their parent topic for reference
            response.children.forEach(subtopic => {
              this.subtopicToTopicMap[subtopic.name_eng] = parentName;
            });
          } else {
            // For deeper levels, also store in subtopics map to ensure tree updates
            this.p2SubTopicsMap[parentName] = topicNames;

            // Map these deeper level topics to their parent
            response.children.forEach(subtopic => {
              this.subtopicToTopicMap[subtopic.name_eng] = parentName;
            });
          }

          // Store topic IDs and metadata for all topics found
          response.children.forEach(topic => {
            // Determine if this topic should have children based on level
            let topicHasChildren = false;

            if (currentLevel === 0) {
              // For sections (level 0), they likely have topics (level 1)
              topicHasChildren = true;
            } else if (currentLevel === 1) {
              // For topics (level 1), they likely have subtopics (level 2)
              topicHasChildren = true;
            } else if (currentLevel === 2) {
              // For subtopics (level 2), they might have children but need explicit check
              // Use a more conservative approach - don't assume children unless we know
              topicHasChildren = false;
            } else {
              // For deeper levels, we don't assume children by default
              topicHasChildren = false;
            }

            // Set topic metadata with appropriate hasChildren flag
            this.topicIdMap[topic.name_eng] = {
              id: topic.topic_id,
              level: topic.item_level,
              hasChildren: topicHasChildren,
              hindiName: topic.name_hindi || undefined
            };

            // Log Hindi name if available
            if (topic.name_hindi) {
       //       console.log(`Topic with Hindi name: ${topic.name_eng} -> ${topic.name_hindi}`);
            }

        //    console.log(`Set topic metadata: ${topic.name_eng} (ID: ${topic.topic_id}, Level: ${topic.item_level}, HasChildren: ${topicHasChildren})`);

            // Also keep the legacy mappings for backward compatibility
            if (currentLevel === 0) {
              // Not used - subjects are at level 0
            } else if (currentLevel === 1) {
              this.level2TopicIdMap[topic.name_eng] = topic.topic_id;
            } else if (currentLevel === 2) {
              this.level3TopicIdMap[topic.name_eng] = topic.topic_id;
            }
          });

          // Emit appropriate event based on level
          if (currentLevel === 0) {
            this.onSectionsLoaded({subject: parentName, sections: topicNames});
          } else if (currentLevel === 1) {
            this.onTopicsLoaded({section: parentName, topics: topicNames});
          } else if (currentLevel >= 2) {
            // For level 2 and deeper, use the subtopics event
            this.onSubtopicsLoaded({topic: parentName, subtopics: topicNames});
          }

          // If there are no children at this level, load questions for this topic
          if (topicNames.length === 0) {
            this.loadQuestionsForTopic(parentName, parentId);
          }

          // Ensure Hindi names are loaded after processing topics
          setTimeout(() => {
            this.loadHindiNamesForUI();
          }, 500);
        },
        error: (error) => {
          console.error(`Error loading topics for ${parentName}:`, error);
        }
      });
  }

  // Load questions for a topic when it has no more children
  loadQuestionsForTopic(topicName: string, topicId: string): void {
    console.log(`Loading questions for topic: ${topicName} (ID: ${topicId})`);

    // Set loading state
    this.isLoadingTopicQuestions = true;

    // Check if TreeService has cached questions from the hierarchy API
    // Pass current language so bilingual mode merges English+Hindi cards
    const cachedQuestions = this.treeService.getHierarchyQuestions(topicName, this.currentLanguage);
    if (cachedQuestions && cachedQuestions.length > 0) {
      console.log(`📚 Using ${cachedQuestions.length} cached hierarchy questions for: ${topicName} (lang=${this.currentLanguage})`);
      this.isLoadingTopicQuestions = false;
      this.loadedQuestions = cachedQuestions;
      return;
    }

    // Fall back to old API if no cached data
    // Important: We're not clearing loadedQuestions here, to avoid flickering
    // when transitioning from one subtopic with questions to another

    this.dataService.getQuestionsByTopicId(topicId, topicName)
      .subscribe({
        next: (questions) => {
          // Reset loading state
          this.isLoadingTopicQuestions = false;

          // Ensure questions is handled as an array
          if (!Array.isArray(questions)) {
            console.error(`API returned non-array response for topic: ${topicName}`, questions);
            // Only clear questions now that we know there are none
            this.loadedQuestions = [];
            this.selectedCard = null;
            return;
          }

          console.log(`Received ${questions.length} questions for topic: ${topicName}`);

          // Now we can safely update loadedQuestions without flickering
          this.loadedQuestions = questions;

          if (questions.length === 0) {
            console.log(`No questions found for topic: ${topicName}. Keeping main and details areas clear.`);
            // Ensure selected card is null to clear details area
            this.selectedCard = null;
          } else {
            console.log(`Successfully loaded ${questions.length} questions for topic: ${topicName}`);
          }
        },
        error: (error) => {
          // Reset loading state
          this.isLoadingTopicQuestions = false;

          console.error(`Error loading questions for topic ${topicName}:`, error);
          // Only clear questions on error
          this.loadedQuestions = [];
          // Ensure selected card is null to clear details area
          this.selectedCard = null;
        }
      });
  }

  loadOcrTestQuestions(): void {
    this.isLoadingTopicQuestions = true;
    this.loadedQuestions = [];
    this.selectedCard = null;
    this.ocrJsonLoaderService.loadOcrQuestions()
      .pipe(finalize(() => { this.isLoadingTopicQuestions = false; }))
      .subscribe({
        next: (questions) => { this.loadedQuestions = questions; },
        error: (err) => { console.error('OCR load error:', err); }
      });
  }

  // DEPRECATED: Use loadTopicTree instead
  loadLevel1Topics(subjectName: string): void {
    console.warn('DEPRECATED: loadLevel1Topics is being called. Use loadTopicTree instead');
    const subjectId = this.subjectIdMap[subjectName];
    if (!subjectId) {
      console.error(`Subject ID not found for subject: ${subjectName}`);
      return;
    }

    // Using 0 for initial level (subjects)
    this.loadTopics(subjectName, subjectId, 0);
  }

  // DEPRECATED: Use loadTopicTree instead
  loadLevel2Topics(sectionName: string): void {
    console.warn('DEPRECATED: loadLevel2Topics is being called. Use loadTopicTree instead');
    const topicInfo = this.topicIdMap[sectionName];
    let sectionId: string;
    let level: number;

    if (topicInfo) {
      // Use the level from the topic metadata
      sectionId = topicInfo.id;
      level = topicInfo.level;
    } else {
      const legacyId = this.level1TopicIdMap[sectionName];
      if (!legacyId) {
        console.error(`Topic ID not found for section: ${sectionName}`);
        return;
      }
      // Legacy fallback using level 1
      sectionId = legacyId;
      level = 1;
    }

    // First load topics for the section to get the list of topics
    this.isLoadingTopics = true;
    this.dataService.getTopicsByLevel(sectionId, level)
      .pipe(
        finalize(() => {
          this.isLoadingTopics = false;
        })
      )
      .subscribe({
        next: (response) => {
          const topicNames = response.children.map(topic => topic.name_eng);
          this.p2TopicsMap[sectionName] = topicNames;

          // First set basic metadata for all topics
          response.children.forEach(topic => {
            this.topicIdMap[topic.name_eng] = {
              id: topic.topic_id,
              level: topic.item_level,
              hasChildren: false, // Default to false, will update after checking
              hindiName: topic.name_hindi || undefined
            };

            // Log Hindi name if available
            if (topic.name_hindi) {
              console.log(`Topic with Hindi name: ${topic.name_eng} -> ${topic.name_hindi}`);
            }

            // Also keep the legacy mappings for backward compatibility
            this.level2TopicIdMap[topic.name_eng] = topic.topic_id;
          });

          // Emit the topics loaded event
          this.onTopicsLoaded({section: sectionName, topics: topicNames});

          // Then preload information about which topics have children
          if (topicNames.length > 0) {
            console.log(`Preloading children info for ${topicNames.length} topics in section ${sectionName}`);
            this.preloadTopicsChildInfo(response.children, topics => {
              // After all topics have been checked, refresh the navigation tree
              this.refreshNavigationTree();

              // Auto-select first topic if this is the selected section and no topic is selected
              if (sectionName === this.selectedSubItem && !this.selectedTopic && topics.length > 0) {
                console.log(`Auto-selecting first topic: ${topics[0]}`);
                this.selectedTopic = topics[0];
                this.loadLevel3Topics(topics[0]);
              }
            });
          }
        },
        error: (error) => {
          console.error(`Error loading topics for section ${sectionName}:`, error);
        }
      });
  }

  // DEPRECATED: Use processTopicTree instead
  preloadTopicsChildInfo(topics: any[], callback: (topics: any[]) => void): void {
    console.warn('DEPRECATED: preloadTopicsChildInfo is being called. Use processTopicTree instead');
    let totalTopics = topics.length;
    let checkedTopics = 0;

    // Check each topic for children
    topics.forEach(topic => {
      this.dataService.getTopicsByLevel(topic.topic_id, topic.item_level)
        .subscribe({
          next: (response) => {
            const hasChildren = response.children.length > 0 || response.has_children;
       //     console.log(`API response for ${topic.name_eng}: has_children=${response.has_children}, actual children=${response.children.length}`);

            // Update the topic metadata
            this.topicIdMap[topic.name_eng].hasChildren = hasChildren;
            // Store Hindi name if available
            if (topic.name_hindi) {
              this.topicIdMap[topic.name_eng].hindiName = topic.name_hindi;
        //      console.log(`Adding Hindi name for ${topic.name_eng}: ${topic.name_hindi}`);
            }

            // If there are children, load them
            if (hasChildren && response.children.length > 0) {
              // Store the children in the appropriate maps
              const childNames = response.children.map(child => child.name_eng);

              // Also store child topic metadata
              response.children.forEach(child => {
                // Determine if this child should have children based on level
                let childHasChildren = false;

                if (child.item_level === 1) {
                  // Topics at level 1 likely have subtopics
                  childHasChildren = true;
                } else if (child.item_level === 2) {
                  // Subtopics at level 2 might have children but need explicit check later
                  childHasChildren = false;
                } else {
                  // For deeper levels, we don't assume children by default
                  childHasChildren = false;
                }

                // Store the child's metadata
                this.topicIdMap[child.name_eng] = {
                  id: child.topic_id,
                  level: child.item_level,
                  hasChildren: childHasChildren,
                  hindiName: child.name_hindi || undefined
                };

              });

              if (topic.item_level === 1) {
                // For Level 1 (sections), store children as topics
                this.p2TopicsMap[topic.name_eng] = childNames;

                // Emit topics loaded event
                this.onTopicsLoaded({section: topic.name_eng, topics: childNames});

                // Load questions for Level 2 (topic)
                if (topic.name_eng === this.selectedSubItem) {
                  response.children.forEach(child => {
                    this.loadQuestionsForTopic(child.name_eng, child.topic_id);
                  });
                }
              } else if (topic.item_level === 2) {
                // For Level 2 (topics), store children as subtopics
                this.p2SubTopicsMap[topic.name_eng] = childNames;

                // Map subtopics to their parent topic for reference
                response.children.forEach(child => {
                  this.subtopicToTopicMap[child.name_eng] = topic.name_eng;

                  // Also keep the legacy mapping
                  this.level3TopicIdMap[child.name_eng] = child.topic_id;
                });

                // Emit subtopics loaded event
                this.onSubtopicsLoaded({topic: topic.name_eng, subtopics: childNames});

                // Load questions if this is the selected topic
                if (topic.name_eng === this.selectedTopic) {
                  response.children.forEach(child => {
                    this.loadQuestionsForTopic(child.name_eng, child.topic_id);
                  });
                }
              } else if (topic.item_level >= 3) {
                // For deeper levels, we directly load questions
                this.loadQuestionsForTopic(topic.name_eng, topic.topic_id);
              }
            } else {
              // No children, load questions
              this.loadQuestionsForTopic(topic.name_eng, topic.topic_id);
            }

            checkedTopics++;
            if (checkedTopics === totalTopics) {
              callback(topics);
            }
          },
          error: (error) => {
            console.error(`Error checking for children of ${topic.name_eng}:`, error);
            checkedTopics++;
            if (checkedTopics === totalTopics) {
              callback([]);
            }
          }
        });
    });
  }

  // DEPRECATED: Use loadQuestionsForTopic instead
  loadQuestionsForSubtopic(subtopicName: string): void {
    console.warn('DEPRECATED: loadQuestionsForSubtopic is being called. Use loadQuestionsForTopic instead');
    const topicInfo = this.topicIdMap[subtopicName];
    let subtopicId: string | undefined;

    if (topicInfo) {
      // First try to see if we can load more nested children
      try {
        // Attempt to load children at the topic's level
        this.loadTopics(subtopicName, topicInfo.id, topicInfo.level);
        return;
      } catch (err) {
        console.log(`Error loading nested children, falling back to questions`, err);
        subtopicId = topicInfo.id;
      }
    } else {
      subtopicId = this.level3TopicIdMap[subtopicName];
    }

    if (!subtopicId) {
      console.error(`Topic ID not found for subtopic: ${subtopicName}`);
      return;
    }

    this.loadQuestionsForTopic(subtopicName, subtopicId);
  }

  // DEPRECATED: Use loadHindiNamesFromTopicMaps instead
  loadHindiNames1(): void {
    console.warn('DEPRECATED: loadHindiNames is being called. Use loadHindiNamesFromTopicMaps instead');
    // Extract Hindi names from topicIdMap
    const hindiNames: {[key: string]: string} = {};

    // Populate with names from topicIdMap
    Object.keys(this.topicIdMap).forEach(key => {
      if (this.topicIdMap[key].hindiName) {
        hindiNames[key] = this.topicIdMap[key].hindiName!;
      }

    });

    // If navigationApp is available, update Hindi names
    if (this.navigationApp) {
      this.navigationApp.updateHindiNames(hindiNames);
    } else {
      console.warn('Navigation app component reference not available yet');
      // Store the Hindi names for later when component is available
      setTimeout(() => {
        if (this.navigationApp) {
          this.navigationApp.updateHindiNames(hindiNames);
        }
      }, 500);
    }
  }

  // DEPRECATED: Use loadSubjectsFromApi instead
  preloadAllSubjectTrees(): void {
    console.warn('DEPRECATED: This method is no longer needed as all trees are loaded with loadSubjectsFromApi');
    // Just call loadSubjectsFromApi which now gets everything in one call
    this.loadSubjectsFromApi();
  }

  // DEPRECATED: Use loadTopicTree instead
  loadLevel3Topics(topicName: string): void {
    console.warn('DEPRECATED: loadLevel3Topics is being called. Use loadTopicTree instead');
    const topicInfo = this.topicIdMap[topicName];
    let topicId: string;
    let level: number;

    if (topicInfo) {
      // Use the level from the topic metadata
      topicId = topicInfo.id;
      level = topicInfo.level;
    } else {
      const legacyId = this.level2TopicIdMap[topicName];
      if (!legacyId) {
        console.error(`Topic ID not found for topic: ${topicName}`);
        return;
      }
      // Legacy fallback using level 2
      topicId = legacyId;
      level = 2;
    }

    // Load subtopics for the topic
    this.isLoadingSubtopics = true;
    this.dataService.getTopicsByLevel(topicId, level)
      .pipe(
        finalize(() => {
          this.isLoadingSubtopics = false;
        })
      )
      .subscribe({
        next: (response) => {
          const subtopicNames = response.children.map(subtopic => subtopic.name_eng);
          this.p2SubTopicsMap[topicName] = subtopicNames;

          // Map subtopics to their parent topic for reference
          response.children.forEach(subtopic => {
            this.subtopicToTopicMap[subtopic.name_eng] = topicName;

            // Store the subtopic ID and metadata
            this.topicIdMap[subtopic.name_eng] = {
              id: subtopic.topic_id,
              level: subtopic.item_level,
              hasChildren: false, // Default to false until we check
              hindiName: subtopic.name_hindi || undefined
            };

            // Log Hindi name if available
            if (subtopic.name_hindi) {
         //     console.log(`Subtopic with Hindi name: ${subtopic.name_eng} -> ${subtopic.name_hindi}`);
            }

            // Also keep the legacy mapping
            this.level3TopicIdMap[subtopic.name_eng] = subtopic.topic_id;
          });

          // Emit the subtopics loaded event
          this.onSubtopicsLoaded({topic: topicName, subtopics: subtopicNames});

          // Auto-select first subtopic if this is the selected topic and no subtopic is selected
          if (topicName === this.selectedTopic && !this.selectedSubTopic && subtopicNames.length > 0) {
            console.log(`Auto-selecting first subtopic: ${subtopicNames[0]}`);
            this.selectedSubTopic = subtopicNames[0];
            // Load questions for this subtopic
            this.loadQuestionsForSubtopic(subtopicNames[0]);
          }

          // Ensure Hindi names are loaded after subtopics are processed
          if (subtopicNames.length > 0) {
            setTimeout(() => {
              this.loadHindiNamesForUI();
            }, 500);
          }
        },
        error: (error) => {
          console.error(`Error loading subtopics for topic ${topicName}:`, error);
        }
      });
  }

  // Add a new method to load the complete topic tree for a subject
  loadTopicTree(subjectName: string, subjectId: string): void {
    console.log(`Loading topic tree for subject ${subjectName}`);

    // If this subject is already present in local maps, do not trigger another API refresh.
    if (this.p1SubMap[subjectName] && this.p1SubMap[subjectName].length > 0) {
      console.log(`Using cached topic tree for subject ${subjectName}`);
      return;
    }

    // Fallback: refresh once only when subject data is genuinely missing.
    console.log(`No cached topic tree for subject ${subjectName}, refreshing from TreeService`);
    this.treeService.refreshTree().catch(error => {
      console.error(`Error refreshing topic tree for subject ${subjectName}:`, error);
    });
  }

  // Clear existing topic maps when loading a new subject tree
  private clearTopicMapsForSubject(subjectName: string): void {
    console.log(`Clearing topic maps for subject: ${subjectName}`);

    // Clear subject-specific maps
    if (this.p1SubMap[subjectName]) {
      // Get sections for this subject
      const sections = [...this.p1SubMap[subjectName]];

      // Clear topics for each section
      sections.forEach(section => {
        if (this.p2TopicsMap[section]) {
          // Get topics for this section
          const topics = [...this.p2TopicsMap[section]];

          // Clear subtopics for each topic
          topics.forEach(topic => {
            delete this.p2SubTopicsMap[topic];
          });

          // Clear topics for this section
          delete this.p2TopicsMap[section];
        }
      });

      // Clear sections for this subject
      delete this.p1SubMap[subjectName];
    }

    console.log('Topic maps cleared');
  }

  // Process the topic tree and update data structures
  private processTopicTree(tree: TopicTreeNode, subjectName: string): void {
    console.log(`Processing topic tree for ${subjectName}`, tree);

    // First, update the topicIdMap with all nodes in the tree
    this.populateTopicIdMapFromTree(tree);

    // Also update the Hindi name map
    this.updateHindiNamesFromTree(tree);

    // Populate sections (level 1) if the tree has children
    if (tree.children && tree.children.length > 0) {
      const sections = tree.children.map(node => node.name_eng);
      this.p1SubMap[subjectName] = sections;

      // For each section, populate topics and subtopics
      tree.children.forEach(sectionNode => {
        if (sectionNode.children && sectionNode.children.length > 0) {
          const sectionName = sectionNode.name_eng;
          const topics = sectionNode.children.map(topicNode => topicNode.name_eng);
          this.p2TopicsMap[sectionName] = topics;

          // For each topic, populate subtopics
          sectionNode.children.forEach(topicNode => {
            if (topicNode.children && topicNode.children.length > 0) {
              const topicName = topicNode.name_eng;
              const subtopics = topicNode.children.map(subtopicNode => subtopicNode.name_eng);
              this.p2SubTopicsMap[topicName] = subtopics;

              // Map subtopics back to their parent topics
              subtopics.forEach((subtopic, index) => {
                this.subtopicToTopicMap[subtopic] = topicName;

                // If this is a new subtopic with no children yet loaded,
                // check if it has children in the tree and update p2SubTopicsMap
                const subtopicNode = topicNode.children[index];
                if (subtopicNode && subtopicNode.children && subtopicNode.children.length > 0) {
                  const deeperSubtopics = subtopicNode.children.map(node => node.name_eng);
                  this.p2SubTopicsMap[subtopic] = deeperSubtopics;

                  // Map these deeper subtopics back to their parent
                  deeperSubtopics.forEach(deeperSubtopic => {
                    this.subtopicToTopicMap[deeperSubtopic] = subtopic;
                  });
                }
              });
            }
          });
        }
      });
    }

    console.log('Updated topic maps:', {
      sections: this.p1SubMap[subjectName],
      topicsMap: this.p2TopicsMap,
      subtopicsMap: this.p2SubTopicsMap
    });
  }

  // Populate the topicIdMap with all nodes from the tree
  private populateTopicIdMapFromTree(node: TopicTreeNode): void {
    // Add current node to the topicIdMap
    this.topicIdMap[node.name_eng] = {
      id: node.topic_id,
      level: node.item_level,
      hasChildren: node.children && node.children.length > 0,
      hindiName: node.name_hindi
    };

    // Recursively process children
    if (node.children && node.children.length > 0) {
      node.children.forEach(childNode => {
        this.populateTopicIdMapFromTree(childNode);
      });
    }
  }

  // Update Hindi names from the tree
  private updateHindiNamesFromTree(node: TopicTreeNode): void {
    // If the node has a Hindi name, update the hindi name map that will be passed to the navigation component
    if (node.name_hindi) {
      // Store Hindi names in a local map
      if (!this.navigationApp) {
        // If navigationApp is not yet available, store in topicIdMap for later use
        this.topicIdMap[node.name_eng] = {
          ...this.topicIdMap[node.name_eng],
          hindiName: node.name_hindi
        };
      } else {
        // Update the Hindi name in the navigation app component
        this.navigationApp.updateHindiNames({ [node.name_eng]: node.name_hindi });
      }
    }

    // Recursively process children
    if (node.children && node.children.length > 0) {
      node.children.forEach(childNode => {
        this.updateHindiNamesFromTree(childNode);
      });
    }
  }

  // Helper method to get subject ID
  private getSubjectId(subjectName: string): string {
    // Check if we have it in the subjectIdMap
    if (this.subjectIdMap[subjectName]) {
      return this.subjectIdMap[subjectName];
    }

    // Check if we have it in the topicIdMap
    if (this.topicIdMap[subjectName]) {
      return this.topicIdMap[subjectName].id;
    }

    return '';
  }

  // Improved method to load Hindi names from the processed topic trees
  loadHindiNamesFromTopicMaps(): void {
    console.log('Loading Hindi names from processed topic trees');

    // Collect all Hindi names from the topicIdMap
    const hindiNames: { [key: string]: string } = {};

    // Extract Hindi names from topicIdMap
    Object.entries(this.topicIdMap).forEach(([topicName, topicInfo]) => {
      if (topicInfo.hindiName) {
        hindiNames[topicName] = topicInfo.hindiName;
      }
    });

    console.log(`Collected ${Object.keys(hindiNames).length} Hindi names to update in navigation component`);

    // Update the navigation component with all collected Hindi names at once
    if (this.navigationApp) {
      this.navigationApp.updateHindiNames(hindiNames);
      console.log('Updated Hindi names in navigation component');
    } else {
      console.warn('Navigation component not available to update Hindi names');
    }
  }

  // Override ngAfterViewInit to make sure we update Hindi names after the navigation component is available
  ngAfterViewInit(): void {
    console.log('AppHomeComponent ngAfterViewInit called');

    // Initialize the mobile view check after the view is initialized
    this.checkIfMobileView();

    // Make sure details area is not visible by default on mobile
    if (this.isMobileView) {
      this.showMobileDetails = false;
    }

    // Wait a bit to ensure navigationApp is initialized
    setTimeout(() => {
      if (this.navigationApp) {
        // Initialize the navigation component with Hindi names from our topic maps
        this.loadHindiNamesFromTopicMaps();
      }
    }, 1000);

    // Listen for window resize events
    window.addEventListener('resize', this.onWindowResize.bind(this));

    // Register event listeners for resizing
    this.registerEventListeners();
  }

  // Add a method to handle layout changes
  onLayoutChange(layoutId: string): void {
    console.log('Layout changed to:', layoutId);

    // If we're already in this layout, don't re-apply it to avoid repeated API calls
    if (this.currentLayout === layoutId) {
      console.log('Already in layout:', layoutId, '- skipping to avoid repeated API calls');
      return;
    }

    // Update the current layout value
    this.currentLayout = layoutId;

    // Handle special mobile navigation toggle event
    if (layoutId === 'toggle_mobile_nav' && this.isMobileView) {
      this.toggleMobileNav();
      return;
    }

    // For mobile view, always use single_tree layout regardless of what was clicked
    if (this.isMobileView) {
      layoutId = 'single_tree';
    }

    // Ensure subjects are loaded before switching to single tree
    if (layoutId === 'single_tree' && (!this.p1Subjects || this.p1Subjects.length === 0)) {
      console.warn('Cannot switch to single tree view - no subjects loaded yet');
      // Load subjects if not already loaded
      if (!this.isLoadingSubjects) {
        console.log('Loading subjects for single tree view...');
        this.loadSubjectsFromApi();
      }
      return;
    }

    // Reset the collapsed states for clarity
    if (layoutId !== 'sidebar') {
      this.isPaneOne_Collapsed = false;
    }
    if (layoutId !== 'collapsed' && layoutId !== 'sidebar') {
      this.isPaneTwo_Collapsed = false;
    }

    switch (layoutId) {
      case 'default':
        console.log('Switching to default layout');

        this.isNavigationCollapsed = false;
        this.isPaneOne_Collapsed = false;
        this.isPaneTwo_Collapsed = false;
        this.showSingleTree = false;
        // Use constants instead of hardcoded values
        this.navigationAreaFlex = `0 0 ${this.DEFAULT_NAV_WIDTH}%`;
        this.mainAreaFlex = `0 0 ${this.DEFAULT_MAIN_WIDTH}%`;
        this.detailsAreaFlex = `0 0 ${this.DEFAULT_DETAILS_WIDTH}%`;

        // Update current width values to match
        this.currentNavWidth = this.DEFAULT_NAV_WIDTH;
        this.currentMainWidth = this.DEFAULT_MAIN_WIDTH;
        this.currentDetailsWidth = this.DEFAULT_DETAILS_WIDTH;
        break;
      case 'sidebar':
        console.log('Switching to sidebar layout');
        this.isNavigationCollapsed = false;
        this.isPaneOne_Collapsed = true;
        this.isPaneTwo_Collapsed = false;
        this.showSingleTree = false;
        // Use constants instead of hardcoded values
        this.navigationAreaFlex = `0 0 ${this.DEFAULT_NAV_WIDTH}%`;
        this.mainAreaFlex = `0 0 ${this.DEFAULT_MAIN_WIDTH}%`;
        this.detailsAreaFlex = `0 0 ${this.DEFAULT_DETAILS_WIDTH}%`;

        // Update current width values to match
        this.currentNavWidth = this.DEFAULT_NAV_WIDTH;
        this.currentMainWidth = this.DEFAULT_MAIN_WIDTH;
        this.currentDetailsWidth = this.DEFAULT_DETAILS_WIDTH;
        break;
      case 'collapsed':
        console.log('Switching to collapsed layout');
        this.isNavigationCollapsed = true;
        this.isPaneOne_Collapsed = false;
        this.isPaneTwo_Collapsed = false;
        this.showSingleTree = false;
        // Use constants instead of hardcoded values
        this.navigationAreaFlex = `0 0 ${this.DEFAULT_NAV_WIDTH}%`;
        this.mainAreaFlex = `0 0 ${this.DEFAULT_MAIN_WIDTH}%`;
        this.detailsAreaFlex = `0 0 ${this.DEFAULT_DETAILS_WIDTH}%`;

        // When navigation is collapsed, this will be handled by updatePaneWidthsOnNavigationChange
        this.updatePaneWidthsOnNavigationChange();
        break;
      case 'single_tree':
        console.log('Switching to single_tree layout');
        console.log('Current data state for single tree:');
        console.log('  p1Subjects:', this.p1Subjects);
        console.log('  p1SubMap:', this.p1SubMap);
        console.log('  p1YearwiseExams:', this.p1YearwiseExams);
        console.log('  p1YearwiseLower:', this.p1YearwiseLower);

        this.isNavigationCollapsed = false;
        this.isPaneOne_Collapsed = false;
        this.isPaneTwo_Collapsed = false;
        this.showSingleTree = true;

        // In single tree mode, main area and details area split available space evenly
        const availableWidth = 100 - this.DEFAULT_NAV_WIDTH;
        const halfAvailableWidth = availableWidth / 2;
        this.mainAreaFlex = `0 0 ${halfAvailableWidth}%`;
        this.detailsAreaFlex = `0 0 ${halfAvailableWidth}%`;

        // Update current width values to match
        this.currentMainWidth = halfAvailableWidth;
        this.currentDetailsWidth = halfAvailableWidth;

        // Ensure Hindi names are loaded for the single tree
        this.loadHindiNamesForUI();

        // Ensure main area is showing in single tree mode
        this.showMains = true;

        // Reset detail pane visibility in mobile view when switching layouts
        if (this.isMobileView) {
          this.showMobileDetails = false;
        }
        break;
    }

    console.log('Current showSingleTree value:', this.showSingleTree);

    // Update navigation state
    if (this.navigationApp) {
      this.navigationApp.isCollapsed = this.isNavigationCollapsed;
      this.navigationApp.isPaneOne_Collapsed = this.isPaneOne_Collapsed;
      this.navigationApp.isPaneTwo_Collapsed = this.isPaneTwo_Collapsed;
    }

    // Save panel sizes for next session
    this.savePanelSizes();
  }

  // Handle search term changes from layout-switcher component
  onSearchTermChange(searchOptions: { term: string, filterMode: boolean }): void {
    console.log('AppHome: onSearchTermChange called with:', searchOptions);

    // Make sure we're in single tree mode when searching
    if (!this.showSingleTree) {
      console.log('AppHome: Switching to single tree mode for search');
      this.onLayoutChange('single_tree');

      // Allow a moment for the component to be rendered before applying search
      // This timeout is now coordinated with the layout-switcher's ngOnChanges
      setTimeout(() => {
        this.applySearchToSingleTree(searchOptions);
      }, 500);
    } else {
      // Already in single tree mode, apply search immediately
      this.applySearchToSingleTree(searchOptions);
    }
  }

  // Helper method to apply search to the single tree component
  private applySearchToSingleTree(searchOptions: { term: string, filterMode: boolean }): void {
    if (this.singleTreeComponent) {
      console.log('AppHome: Applying search to singleTreeComponent', searchOptions);

      // Update the search parameters
      this.singleTreeComponent.searchTerm = searchOptions.term;
      this.singleTreeComponent.filterMode = searchOptions.filterMode;

      // Explicitly call performSearch() to ensure the search is executed
      this.singleTreeComponent.performSearch();

      // Force a double-check after a small delay to ensure highlighting and filtering
      setTimeout(() => {
        if (this.singleTreeComponent &&
            (this.singleTreeComponent.searchTerm !== searchOptions.term ||
             this.singleTreeComponent.filterMode !== searchOptions.filterMode)) {
          console.log('AppHome: Reapplying search because parameters were out of sync');
          this.singleTreeComponent.searchTerm = searchOptions.term;
          this.singleTreeComponent.filterMode = searchOptions.filterMode;
          this.singleTreeComponent.performSearch();
        }
      }, 300);
    } else {
      console.warn('AppHome: singleTreeComponent is not available for search!');
    }
  }

  // Handle show details change from main area component
  onShowDetailsChange(showDetails: boolean): void {
    if (!showDetails && this.showDetails) {
      // Hiding details: save current flex and expand main
      this.savedMainAreaFlex = this.mainAreaFlex;
      this.savedDetailsAreaFlex = this.detailsAreaFlex;
      this.mainAreaFlex = '1 1 auto';
    } else if (showDetails && !this.showDetails) {
      // Showing details: restore saved flex values
      if (this.savedMainAreaFlex) {
        this.mainAreaFlex = this.savedMainAreaFlex;
        this.detailsAreaFlex = this.savedDetailsAreaFlex;
      } else {
        this.mainAreaFlex = `0 0 ${this.DEFAULT_MAIN_WIDTH}%`;
        this.detailsAreaFlex = `0 0 ${this.DEFAULT_DETAILS_WIDTH}%`;
      }
    }
    this.showDetails = showDetails;
    // Give the UI time to update when details pane visibility changes
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }


  // DEPRECATED: Use loadHindiNamesFromTopicMaps instead
  private loadHindiNames_DEPRECATED(): void {
    console.warn('DEPRECATED: loadHindiNames_DEPRECATED is being called. Use loadHindiNamesFromTopicMaps instead');
    // Extract Hindi names from topicIdMap
    const hindiNames: {[key: string]: string} = {};

    // Populate with names from topicIdMap
    Object.keys(this.topicIdMap).forEach(key => {
      if (this.topicIdMap[key].hindiName) {
        hindiNames[key] = this.topicIdMap[key].hindiName!;
      }
    });

    // If navigationApp is available, update Hindi names
    if (this.navigationApp) {
      this.navigationApp.updateHindiNames(hindiNames);
    } else {
      console.warn('Navigation app component reference not available yet');
      // Store the Hindi names for later when component is available
      setTimeout(() => {
        if (this.navigationApp) {
          this.navigationApp.updateHindiNames(hindiNames);
        }
      }, 500);
    }
  }

  // DEPRECATED: Use loadHindiNamesFromTopicMaps instead
  loadHindiNames_LEGACY(): void {
    console.warn('DEPRECATED: loadHindiNames_LEGACY is being called. Use loadHindiNamesFromTopicMaps instead');
    // Extract Hindi names from topicIdMap
    const hindiNames: {[key: string]: string} = {};

    // Populate with names from topicIdMap
    Object.keys(this.topicIdMap).forEach(key => {
      if (this.topicIdMap[key].hindiName) {
        hindiNames[key] = this.topicIdMap[key].hindiName!;
      }
    });

    // If navigationApp is available, update Hindi names
    if (this.navigationApp) {
      this.navigationApp.updateHindiNames(hindiNames);
    } else {
      console.warn('Navigation app component reference not available yet');
      // Store the Hindi names for later when component is available
      setTimeout(() => {
        if (this.navigationApp) {
          this.navigationApp.updateHindiNames(hindiNames);
        }
      }, 500);
    }
  }

  // New function for UI components to request Hindi names
  loadHindiNamesForUI(): void {
    console.log('Loading Hindi names for UI components');

    // Prevent executing this multiple times in rapid succession
    if (this._isLoadingHindiNames) {
      console.log('Hindi name loading already in progress, skipping duplicate request');
      return;
    }

    this._isLoadingHindiNames = true;

    // Use setTimeout to avoid blocking the UI thread
    setTimeout(() => {
      try {
        // Start with basic hardcoded names for common subjects
        const basicHindiNames = {
          'Mathematics': 'गणित',
          'Physics': 'भौतिकी',
          'Chemistry': 'रसायन विज्ञान',
          'Computer Science': 'कंप्यूटर विज्ञान',
          'Algebra': 'बीजगणित',
          'Calculus': 'कैलकुलस',
          'Mechanics': 'यांत्रिकी',
          'Optics': 'प्रकाशिकी',
          'Electronics': 'इलेक्ट्रॉनिक्स',
          'History': 'इतिहास',
          'Geography': 'भूगोल',
          'Science': 'विज्ञान',
          'Polity': 'राजव्यवस्था'
        };

        // Process the topicIdMap in smaller batches
        this.processHindiNamesInBatches(basicHindiNames);
      } catch (error) {
        console.error('Error loading Hindi names:', error);
        this._isLoadingHindiNames = false;
      }
    }, 100);
  }

  // Process Hindi names in smaller batches to avoid freezing the UI
  private _isLoadingHindiNames = false;
  private processHindiNamesInBatches(initialNames: {[key: string]: string}): void {
    // Start with initial names
    const allHindiNames = {...initialNames};

    // Extract topic names from topicIdMap
    const topicNames = Object.keys(this.topicIdMap);
    const batchSize = 50; // Process 50 names at a time
    let currentIndex = 0;

    const processNextBatch = () => {
      // Calculate end index for current batch
      const endIndex = Math.min(currentIndex + batchSize, topicNames.length);
      console.log(`Processing Hindi names batch ${currentIndex}-${endIndex} of ${topicNames.length} total names`);

      // Process this batch
      for (let i = currentIndex; i < endIndex; i++) {
        const topicName = topicNames[i];
        const topicInfo = this.topicIdMap[topicName];

        if (topicInfo && topicInfo.hindiName) {
          allHindiNames[topicName] = topicInfo.hindiName;
        }
      }

      // Update current index
      currentIndex = endIndex;

      // If we have more batches to process, schedule next batch
      if (currentIndex < topicNames.length) {
        setTimeout(() => processNextBatch(), 0);
      } else {
        // We're done, update the hindi name map and navigation component
        this.hindiNameMap = allHindiNames;
        console.log('Hindi names processed:', Object.keys(this.hindiNameMap).length);

        // Update the navigation component with Hindi names if available
        if (this.navigationApp) {
          this.navigationApp.updateHindiNames(this.hindiNameMap);
        }

        // Clear the loading flag
        this._isLoadingHindiNames = false;
      }
    };

    // Start the first batch
    processNextBatch();
  }

  // Handle view mode changes from the main-area component
  onViewModeChange(viewMode: string): void {
    console.log('View mode changed to:', viewMode);
    if (viewMode === 'fullscreen' || viewMode === 'split') {
      this.viewMode = viewMode as 'split' | 'fullscreen';
    }

    // Adjust the layout based on the view mode
    if (viewMode === 'fullscreen') {
        this.mainAreaFlex = '0 0 0%';
        this.detailsAreaFlex = '1 1 100%';
    } else if (viewMode === 'split') {
      this.resetPanelSizes();
    }

    this.savePanelSizes();
    this.cdr.detectChanges();
  }

  /**
   * Handle language change from main-area or single-tree.
   * Re-fetches questions from the hierarchy cache with the correct language,
   * and syncs the language selection across navigation and single-tree components.
   */
  onLanguageChange(language: string): void {
    console.log('🌐 Language changed to:', language);
    this.currentLanguage = language;

    // Reload questions from cache with the new language
    const topicName = this.selectedSubTopic || this.selectedTopic || this.selectedSubItem;
    if (topicName && this.treeService.isUsingHierarchyApi()) {
      const cachedQuestions = this.treeService.getHierarchyQuestions(topicName, language);
      if (cachedQuestions && cachedQuestions.length > 0) {
        console.log(`🌐 Reloaded ${cachedQuestions.length} questions for ${topicName} in ${language} mode`);
        this.loadedQuestions = cachedQuestions;
      }
    }

    // Update language in navigation component if available
    if (this.navigationApp) {
      this.navigationApp.selectedLanguage = language;
      this.navigationApp.onLanguageChange(language);
    }

    // Update language in single-tree component if available
    if (this.singleTreeComponent) {
      this.singleTreeComponent.selectedLanguage = language;
      // No need to call onLanguageChange directly as it was the source of this event
    }

    // If Hindi names aren't loaded yet, load them
    if (language === 'hindionly' || language === 'bilingual') {
      this.loadHindiNamesForUI();
    }
  }

  // Method to handle showing/hiding main area when in fullscreen mode
  onShowMainsChange(showMains: boolean): void {
    console.log('Show mains changed to:', showMains);
    this.showMains = showMains;

    // When showing mains, we need to adjust the layout properly
    // to ensure close button remains visible
    if (showMains) {
      // Reset to split mode with proper proportions
      this.viewMode = 'split';
      this.mainAreaFlex = '0 0 40%';
      this.detailsAreaFlex = '0 0 40%';
    }

    this.cdr.detectChanges();
  }

  // Check if in mobile view based on screen width
  private checkIfMobileView(): void {
    this.isMobileView = window.innerWidth <= 768;

    // In mobile view, show only the single tree when navigation is visible
    if (this.isMobileView) {
      this.showSingleTree = true;
      // Don't reset mobile details on initial check - only hide mobile nav
      this.showMobileNav = false;

      // Ensure data is loaded for mobile view
      if ((!this.p1Subjects || this.p1Subjects.length === 0) && !this.isLoadingSubjects) {
        console.log('Mobile view detected - initiating subject data loading');
        this.loadSubjectsFromApi();
      }

      // When in mobile view, ensure tree data is loaded and expanded
      setTimeout(() => {
        if (this.singleTreeComponent) {
          console.log('Mobile view detected - ensuring tree data is updated and expanded');
          this.singleTreeComponent.updateTreeData();
          this.singleTreeComponent.expandAllNodes();
          // Also expand yearwise tree if in that mode
          if (this.p1Mode === 'yearwise') {
            this.singleTreeComponent.updateYearwiseTreeData();
            this.singleTreeComponent.expandAllYearwiseNodes();
          }
        }
      }, 500);

      // We don't reset showMobileDetails here because it would prevent details from showing
      // when clicking on a question
    }
  }

  // Handle window resize events
  private onWindowResize(): void {
    const wasMobile = this.isMobileView;
    this.checkIfMobileView();

    // If switching between mobile and desktop view, reset mobile-specific states
    if (wasMobile !== this.isMobileView) {
      if (!this.isMobileView) {
        // Switching to desktop view
        this.showMobileNav = false;
        this.showMobileDetails = false;
      } else {
        // Switching to mobile view
        this.showMobileDetails = false;
        this.showMobileNav = false;
      }
    }
  }

  // Toggle mobile navigation visibility
  toggleMobileNav(): void {
    // Toggle the mobile navigation state
    this.showMobileNav = !this.showMobileNav;

    // When showing mobile nav, hide details view
    if (this.showMobileNav) {
      this.showMobileDetails = false;
    }

    console.log(`Mobile navigation toggled: ${this.showMobileNav ? 'showing' : 'hiding'} navigation`);
    console.log('Current mobile view state - Nav visible:', this.showMobileNav,
                'Details visible:', this.showMobileDetails);
  }

  // Toggle mobile details visibility
  toggleMobileDetails(show: boolean): void {
    console.log(`Toggling mobile details visibility: ${show ? 'showing' : 'hiding'} details`);
    console.log('Current view state - Mobile view:', this.isMobileView,
                'Nav visible:', this.showMobileNav,
                'Details visible:', this.showMobileDetails);

    this.showMobileDetails = show;

    // When showing details, hide nav
    if (this.showMobileDetails) {
      this.showMobileNav = false;
    }

    console.log('New view state - Mobile view:', this.isMobileView,
               'Nav visible:', this.showMobileNav,
               'Details visible:', this.showMobileDetails);
  }

  // Back to main area from details on mobile
  backToMainFromDetails(): void {
    this.showMobileDetails = false;
  }

  // Add HostListener for window resize
  @HostListener('window:resize')
  onResize() {
    this.checkIfMobileView();
  }

  // Handle font size changes from navigation-app
  onFontSizeChange(size: FontSizeOption): void {
    this.fontSizeService.setFontSize(size);
  }

  // Handle edit mode changes from navigation-app
  onEditModeChange(isEditMode: boolean): void {
    console.log('Edit mode changed:', isEditMode);

    if (isEditMode) {
      // When edit mode is enabled, set navigation width to 50%
      this.currentNavWidth = 50;
      this.navigationAreaFlex = `0 0 ${this.currentNavWidth}%`;
      this.saveNavPanelSize();
    } else {
      // When edit mode is disabled, restore default navigation width
      this.currentNavWidth = this.DEFAULT_NAV_WIDTH;
      this.navigationAreaFlex = `0 0 ${this.currentNavWidth}%`;
      this.saveNavPanelSize();
    }
  }

  // Add these stub methods for the event handlers

  // Event handlers matching what we added to the HTML
  onModeChange(mode: 'topicwise' | 'yearwise'): void {
    this.onP1ModeChange(mode);
  }

  onSubjectClick(subject: string): void {
    this.onSubjectSelect(subject);
  }

  onSubItemClick(subItem: string): void {
    this.onSubItemSelect(subItem);
  }

  onTopicClick(topic: string): void {
    this.onTopicSelect(topic);
  }

  onSubTopicClick(subTopic: string): void {
    this.onSubTopicSelect(subTopic);
  }

  onYearwiseExamClick(exam: string): void {
    this.onYearwiseExamSelect(exam);
  }

  onYearwiseLowerClick(lower: string): void {
    this.onYearwiseLowerSelect(lower);
  }

  onYearClick(year: string): void {
    this.onYearSelect(year);
  }

  onViewButtonClick(): void {
    // Handle view button click
    console.log('View button clicked');
  }

  onSettingsButtonClick(): void {
    // Handle settings button click
    console.log('Settings button clicked');
  }

  // Handle refresh tree request from navigation-app component
  onRefreshTreeRequested(): void {
    console.log('App-Home: Received refreshTreeRequested event from navigation component');

    // Check if we're already refreshing
    if (this.isLoadingSubjects || this.isRefreshingNavigationTree) {
      console.log('App-Home: Tree refresh already in progress, ignoring request');
      return;
    }

    // Ensure navigation is visible before refreshing
    this.resetToDefaultView();

    // Show loading indicator or feedback that refresh is happening
    this.isLoadingSubjects = true;
    this.isRefreshingNavigationTree = true;

    // First refresh the tree data, then update UI when complete
    this.treeService.refreshTree()
      .then(() => {
        console.log('App-Home: Tree refresh completed successfully');

        // Reset loading flags
        this.isLoadingSubjects = false;
        this.isRefreshingNavigationTree = false;

        // The tree data will be automatically updated via subscription in any component
        // that needs it, so we don't need to manually update or trigger the navigation
        // tree refresh again which would cause duplicate calls
      })
      .catch(err => {
        console.error('App-Home: Error refreshing tree:', err);

        // Reset loading flags even on error
        this.isLoadingSubjects = false;
        this.isRefreshingNavigationTree = false;
      });
  }

  // ─────────────────────────────────────────────────────────
  // Event registration for resize tracking
  // ─────────────────────────────────────────────────────────
  private registerEventListeners(): void {
    window.addEventListener('mousemove', this.mouseMoveHandler);
    window.addEventListener('mouseup', this.mouseUpHandler);
    window.addEventListener('mousemove', this.navMouseMoveHandler);
    window.addEventListener('mouseup', this.navMouseUpHandler);
  }

  private unregisterEventListeners(): void {
    window.removeEventListener('mousemove', this.mouseMoveHandler);
    window.removeEventListener('mouseup', this.mouseUpHandler);
    window.removeEventListener('mousemove', this.navMouseMoveHandler);
    window.removeEventListener('mouseup', this.navMouseUpHandler);
  }

  // Bound handlers to maintain "this" context
  private mouseMoveHandler = (event: MouseEvent) => this.onMouseMove(event);
  private mouseUpHandler = () => this.onMouseUp();
  private navMouseMoveHandler = (event: MouseEvent) => this.onNavMouseMove(event);
  private navMouseUpHandler = () => this.onNavMouseUp();

  // ─────────────────────────────────────────────────────────
  // Resizing functionality for main/details divider
  // ─────────────────────────────────────────────────────────

  // Start resize for navigation divider
  onNavMouseDown(event: MouseEvent): void {
    event.preventDefault();
    this.isNavDragging = true;
    this.startNavX = event.clientX;

    // Get current width as a number
    this.currentNavWidth = parseFloat(this.navigationAreaFlex.split('%')[0]);

    // Add global class to prevent text selection during resize
    document.body.classList.add('user-select-none');
  }

  // Handle mouse move for navigation divider
  onNavMouseMove(event: MouseEvent): void {
    if (!this.isNavDragging) return;

    // Calculate how much the mouse has moved
    const dx = event.clientX - this.startNavX;

    // Get the container width
    const container = document.querySelector('.app-content');
    if (!container) return;
    const containerWidth = container.getBoundingClientRect().width;

    // Convert mouse movement to percentage of container width
    const percentChange = (dx / containerWidth) * 100;

    // Calculate new width based on the original width plus the change
    let newNavWidth = this.currentNavWidth + percentChange;

    // Ensure the width is within bounds
    const constrainedWidth = Math.max(
      this.MIN_NAV_WIDTH,
      Math.min(newNavWidth, this.MAX_NAV_WIDTH)
    );

    // Update flex values
    this.navigationAreaFlex = `0 0 ${constrainedWidth.toFixed(2)}%`;

    // Adjust the main and details areas to maintain proportions
    const remainingWidth = 100 - constrainedWidth;

    if (this.showDetails) {
      // Maintain the ratio between main and details areas
      const mainRatio = this.currentMainWidth / (this.currentMainWidth + this.currentDetailsWidth);
      const detailsRatio = this.currentDetailsWidth / (this.currentMainWidth + this.currentDetailsWidth);

      const newMainWidth = remainingWidth * mainRatio;
      const newDetailsWidth = remainingWidth * detailsRatio;

      this.mainAreaFlex = `0 0 ${newMainWidth.toFixed(2)}%`;
      this.detailsAreaFlex = `0 0 ${newDetailsWidth.toFixed(2)}%`;
    } else {
      // If details are hidden, main takes all remaining width
      this.mainAreaFlex = `0 0 ${remainingWidth.toFixed(2)}%`;
    }

    // Force update
    this.cdr.detectChanges();
  }

  // End resize for navigation divider
  onNavMouseUp(): void {
    if (!this.isNavDragging) return;

    this.isNavDragging = false;

    // Remove event listeners
    document.removeEventListener('mousemove', this.navMouseMoveHandler);
    document.removeEventListener('mouseup', this.navMouseUpHandler);

    // Get final width from the flex string and update current width
    const navWidthMatch = this.navigationAreaFlex.match(/0 0 (\d+(?:\.\d+)?)%/);
    if (navWidthMatch && navWidthMatch[1]) {
      this.currentNavWidth = parseFloat(navWidthMatch[1]);
    }

    // Save the new panel sizes to localStorage
    this.saveNavPanelSize();

    // Also update the main and details current widths
    const mainWidthMatch = this.mainAreaFlex.match(/0 0 (\d+(?:\.\d+)?)%/);
    if (mainWidthMatch && mainWidthMatch[1]) {
      this.currentMainWidth = parseFloat(mainWidthMatch[1]);
    }

    const detailsWidthMatch = this.detailsAreaFlex.match(/0 0 (\d+(?:\.\d+)?)%/);
    if (detailsWidthMatch && detailsWidthMatch[1]) {
      this.currentDetailsWidth = parseFloat(detailsWidthMatch[1]);
    }

    console.log(`Navigation resize completed - Nav: ${this.currentNavWidth.toFixed(2)}%, Main: ${this.currentMainWidth.toFixed(2)}%, Details: ${this.currentDetailsWidth.toFixed(2)}%`);
  }

  // Update the navigation path from hierarchy path
  onHierarchyPathChange(pathItems: string[]): void {
    console.log('Hierarchy path changed:', pathItems);

    // Convert string array to NavigationPathItem objects
    const navigationItems = pathItems.map((name, index) => {
      let type: 'subject' | 'section' | 'topic' | 'subtopic' | 'exam' | 'paper' | 'year' = 'subject'; // Default

      // Determine the type based on position and mode
      if (this.p1Mode === 'topicwise') {
        if (index === 0) type = 'subject';
        else if (index === 1) type = 'section';
        else if (index === 2) type = 'topic';
        else if (index === 3) type = 'subtopic';
      } else { // yearwise
        if (index === 0) type = 'exam';
        else if (index === 1) type = 'paper';
        else if (index === 2) type = 'year';
      }

      // Get hindi name from map if available
      const hindiName = this.hindiNameMap[name] || '';

      return { name, type, hindiName };
    });

    // Update the navigation path service
    this.navigationPathService.updateNavigationPath(navigationItems);
  }

  // Method to handle closing the details area
  onCloseDetails(): void {
    console.log('Closing details area');
    this.showDetails = false;
    this.mainAreaFlex = '1 1 auto';

    // Make sure that both the app-home and main-area components
    // are aware of the showDetails state change
    this.cdr.detectChanges();
  }

  // Update UI with tree data
  private updateUIWithTreeData(treeData: any): void {
    if (!treeData || !treeData.subjects) {
      console.error('Invalid tree data received, cannot update UI');
      return;
    }

    // Update subject list
    this.p1Subjects = Object.keys(treeData.subjects);

    // Update subject->section map
    this.p1SubMap = {};
    Object.keys(treeData.subjects).forEach(subject => {
      this.p1SubMap[subject] = Object.keys(treeData.subjects[subject].sections || {});
    });

    // Update section->topic map
    this.p2TopicsMap = {};
    Object.keys(treeData.subjects).forEach(subject => {
      Object.keys(treeData.subjects[subject].sections || {}).forEach(section => {
        this.p2TopicsMap[section] = Object.keys(treeData.subjects[subject].sections[section].topics || {});
      });
    });

    // Update topic->subtopic map
    this.p2SubTopicsMap = {};
    Object.keys(treeData.subjects).forEach(subject => {
      Object.keys(treeData.subjects[subject].sections || {}).forEach(section => {
        Object.keys(treeData.subjects[subject].sections[section].topics || {}).forEach(topic => {
          this.p2SubTopicsMap[topic] = Object.keys(
            treeData.subjects[subject].sections[section].topics[topic].subtopics || {}
          );
        });
      });
    });

    // Update topic ID map for lookup
    this.topicIdMap = {};
    Object.keys(treeData.subjects).forEach(subject => {
      // Add subject
      this.topicIdMap[subject] = {
        id: treeData.subjects[subject].id,
        level: 0,
        hasChildren: Object.keys(treeData.subjects[subject].sections || {}).length > 0,
        hindiName: treeData.subjects[subject].hindiName
      };

      // Add sections
      Object.keys(treeData.subjects[subject].sections || {}).forEach(section => {
        this.topicIdMap[section] = {
          id: treeData.subjects[subject].sections[section].id,
          level: 1,
          hasChildren: Object.keys(treeData.subjects[subject].sections[section].topics || {}).length > 0,
          hindiName: treeData.subjects[subject].sections[section].hindiName
        };

        // Add topics
        Object.keys(treeData.subjects[subject].sections[section].topics || {}).forEach(topic => {
          this.topicIdMap[topic] = {
            id: treeData.subjects[subject].sections[section].topics[topic].id,
            level: 2,
            hasChildren: Object.keys(treeData.subjects[subject].sections[section].topics[topic].subtopics || {}).length > 0,
            hindiName: treeData.subjects[subject].sections[section].topics[topic].hindiName
          };

          // Add subtopics
          Object.keys(treeData.subjects[subject].sections[section].topics[topic].subtopics || {}).forEach(subtopic => {
            this.topicIdMap[subtopic] = {
              id: treeData.subjects[subject].sections[section].topics[topic].subtopics[subtopic].id,
              level: 3,
              hasChildren: false,
              hindiName: treeData.subjects[subject].sections[section].topics[topic].subtopics[subtopic].hindiName
            };
          });
        });
      });
    });

    // Update hindi name map
    this.hindiNameMap = {};
    Object.keys(this.topicIdMap).forEach(name => {
      if (this.topicIdMap[name].hindiName) {
        this.hindiNameMap[name] = this.topicIdMap[name].hindiName;
      }
    });

    // Notify components that tree data has been updated
    if (this.navigationApp) {
      this.navigationApp.updateTreeData();
    } else {
      console.warn('navigationApp reference is null, cannot update tree');
    }

    if (this.singleTreeComponent) {
      this.singleTreeComponent.updateTreeData();
    } else {
      console.warn('singleTreeComponent reference is null, cannot update tree');
    }

    // Force a refresh of the UI
    this.cdr.detectChanges();

    // Try to auto-select first subject if none is selected
    if (this.p1Subjects.length > 0 && !this.selectedSubject) {
      setTimeout(() => {
        this.onSubjectSelect(this.p1Subjects[0]);
      }, 100);
    }
  }

  // Reset to default view, ensuring navigation is displayed
  private resetToDefaultView(): void {
    // Make sure navigation is visible and single tree is hidden
    this.showSingleTree = false;

    // Set to default layout
    this.currentLayout = 'default';

    // Reset all collapsed states
    this.isNavigationCollapsed = false;
    this.isPaneOne_Collapsed = false;
    this.isPaneTwo_Collapsed = false;

    // Set flex values using constants instead of hardcoded values
    this.navigationAreaFlex = `0 0 ${this.DEFAULT_NAV_WIDTH}%`;
    this.mainAreaFlex = `0 0 ${this.DEFAULT_MAIN_WIDTH}%`;
    this.detailsAreaFlex = `0 0 ${this.DEFAULT_DETAILS_WIDTH}%`;

    // Update current width values to match
    this.currentNavWidth = this.DEFAULT_NAV_WIDTH;
    this.currentMainWidth = this.DEFAULT_MAIN_WIDTH;
    this.currentDetailsWidth = this.DEFAULT_DETAILS_WIDTH;

    // Force change detection
    this.cdr.detectChanges();

    // Force layout refresh
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }

  // Save navigation panel size to local storage
  saveNavPanelSize(): void {
    try {
      console.log('Saving navigation panel size:', {
        navigationAreaFlex: this.navigationAreaFlex
      });

      localStorage.setItem('navigationAreaFlex', this.navigationAreaFlex);
    } catch (e) {
      console.error('Error saving navigation panel size:', e);
    }
  }

  // Add a method to clear localStorage values for panel sizes
  private clearSavedPanelSizes(): void {
    try {
      console.log('Clearing saved panel sizes from localStorage');
      localStorage.removeItem('mainAreaFlex');
      localStorage.removeItem('detailsAreaFlex');
      localStorage.removeItem('navigationAreaFlex');
      localStorage.removeItem('panelSizes');
    } catch (e) {
      console.error('Error clearing saved panel sizes:', e);
    }
  }

  // Add a debugging method to log current widths
  private logCurrentWidthSettings(): void {
    console.log('=== CURRENT WIDTH SETTINGS ===');
    console.log(`Constants: NAV=${this.DEFAULT_NAV_WIDTH}%, MAIN=${this.DEFAULT_MAIN_WIDTH}%, DETAILS=${this.DEFAULT_DETAILS_WIDTH}%`);
    console.log(`Current widths: NAV=${this.currentNavWidth}%, MAIN=${this.currentMainWidth}%, DETAILS=${this.currentDetailsWidth}%`);
    console.log(`Flex values: NAV=${this.navigationAreaFlex}, MAIN=${this.mainAreaFlex}, DETAILS=${this.detailsAreaFlex}`);
    console.log('============================');
  }
}
